<template>
  <ErrorBoundary 
    :error-title="t('userPanel.meta.errorTitle')" 
    :error-message="t('userPanel.meta.errorMessage')"
  >
    <div class="relative flex min-h-screen flex-col bg-slate-50 justify-between overflow-x-hidden font-sans pt-5 w-full max-w-full">
      
      <!-- Layout principal con sidebar -->
      <div class="flex flex-1 w-full max-w-full">
        
        <!-- SIDEBAR - Similar a ApplicantSidebar -->
        <div 
          v-if="showSidebar"
          class="hidden lg:flex lg:flex-col lg:w-80 lg:fixed lg:inset-y-0 lg:left-0"
        >
          <!-- Header del sidebar -->
          <div class="flex items-center justify-between p-4 border-b border-gray-200 mt-16">
            <h3 class="text-lg font-bold text-gray-900 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-gray-900" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd" />
              </svg>
              {{ t('userPanel.sidebar.title') }}
            </h3>
            <button
              @click="showSidebar = false"
              class="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all duration-200"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
              </svg>
            </button>
          </div>

          <!-- Navegación del sidebar -->
          <div class="flex-1 overflow-y-auto no-scrollbar p-4">
            
            <!-- Progreso general -->
            <div class="mb-6">
              <div class="p-4 rounded-xl bg-white border border-gray-300">
                <div class="text-center">
                  <div class="text-3xl font-bold text-gray-900 mb-1">{{ Math.round(overallProfileProgress) }}%</div>
                  <div class="text-xs text-gray-800 font-medium mb-3">{{ t('userPanel.sidebar.profileCompleted') }}</div>
                  <div class="w-full bg-gray-200 rounded-full h-2 mb-2">
                    <div 
                      class="bg-gray-900 h-2 rounded-full transition-all duration-300"
                      :style="{ width: `${overallProfileProgress}%` }"
                    ></div>
                  </div>
                  <p class="text-xs text-gray-700">
                    {{ t('userPanel.sidebar.itemsCompleted', { completed: getCompletedItems(), total: getTotalItems() }) }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Widget: Puntuación como candidato -->
            <div class="mb-5">
              <div class="p-4 rounded-xl border bg-white border-gray-300">
                <div class="flex items-center justify-between mb-2">
                  <span class="text-xs font-semibold uppercase tracking-wide text-gray-900">
                    {{ t('userPanel.scoreWidget.title') }}
                  </span>
                  <span class="text-xs font-bold px-2 py-0.5 rounded-full bg-gray-200 text-gray-900">
                    {{ agentViewScore.score }} / {{ agentViewScore.MAX_SCORE }}
                  </span>
                </div>
                <!-- Barra de progreso del score -->
                <div class="w-full bg-gray-200 rounded-full h-2 mb-3">
                  <div
                    class="h-2 rounded-full transition-all duration-500 bg-gray-900"
                    :style="{ width: `${agentViewScore.percentage}%` }"
                  ></div>
                </div>
                <!-- Desglose de criterios -->
                <ul class="space-y-1 text-xs">
                  <li class="flex items-center justify-between">
                    <span :class="agentViewScore.breakdown.identity ? 'text-gray-700' : 'text-gray-400'">
                      {{ agentViewScore.breakdown.identity ? '✅' : '⬜' }} {{ t('userPanel.scoreWidget.identity') }}
                    </span>
                    <span class="font-medium" :class="agentViewScore.breakdown.identity ? 'text-gray-700' : 'text-gray-400'">+{{ agentViewScore.breakdown.identity }}</span>
                  </li>
                  <li class="flex items-center justify-between">
                    <span :class="agentViewScore.breakdown.documents ? 'text-gray-700' : 'text-gray-400'">
                      {{ agentViewScore.breakdown.documents ? '✅' : '⬜' }} {{ t('userPanel.scoreWidget.documents') }}
                    </span>
                    <span class="font-medium" :class="agentViewScore.breakdown.documents ? 'text-gray-700' : 'text-gray-400'">+{{ agentViewScore.breakdown.documents }}</span>
                  </li>
                  <li v-if="agentViewScore.breakdown.completionBonus" class="flex items-center justify-between">
                    <span class="text-gray-700">✅ {{ t('userPanel.scoreWidget.completionBonus') }}</span>
                    <span class="font-medium text-gray-700">+{{ agentViewScore.breakdown.completionBonus }}</span>
                  </li>
                  <li class="flex items-center justify-between">
                    <span :class="agentViewScore.breakdown.guarantor ? 'text-gray-700' : 'text-gray-400'">
                      {{ agentViewScore.breakdown.guarantor ? '✅' : '⬜' }} {{ t('userPanel.scoreWidget.guarantor') }}
                    </span>
                    <span class="font-medium" :class="agentViewScore.breakdown.guarantor ? 'text-gray-700' : 'text-gray-400'">+{{ agentViewScore.breakdown.guarantor }}</span>
                  </li>
                  <li v-if="agentViewScore.breakdown.seniority > 0" class="flex items-center justify-between">
                    <span class="text-gray-700">✅ {{ t('userPanel.scoreWidget.seniority') }}</span>
                    <span class="font-medium text-gray-700">+{{ agentViewScore.breakdown.seniority }}</span>
                  </li>
                </ul>
                <!-- CTA si el score es mejorable -->
                <p v-if="agentViewScore.level !== 'excellent'" class="mt-2 text-xs leading-snug text-gray-700">
                  {{
                    !agentViewScore.breakdown.identity && !agentViewScore.breakdown.documents
                      ? t('userPanel.scoreWidget.cta.noIdentityNoDocuments')
                      : !agentViewScore.breakdown.identity
                        ? t('userPanel.scoreWidget.cta.noIdentity')
                        : !agentViewScore.breakdown.documents
                          ? t('userPanel.scoreWidget.cta.noDocuments')
                          : !agentViewScore.breakdown.guarantor
                            ? t('userPanel.scoreWidget.cta.noGuarantor')
                            : t('userPanel.scoreWidget.cta.almostComplete')
                  }}
                </p>
              </div>
            </div>

            <!-- Opciones de navegación -->
            <nav class="space-y-2">
              <!-- Opción: Perfil -->
              <button
                @click="activeSection = 'profile'"
                :class="[
                  'w-full flex items-center justify-between p-4 rounded-xl transition-all duration-200',
                  activeSection === 'profile' 
                    ? 'bg-gray-900 text-white shadow-lg' 
                    : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300'
                ]"
              >
                <div class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-3" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd" />
                  </svg>
                  <span class="font-medium">{{ t('userPanel.navigation.profile') }}</span>
                </div>
                <div class="flex items-center">
                  <span :class="[activeSection === 'profile' ? 'bg-white/20' : 'bg-gray-100 text-gray-700', 'text-xs px-2 py-1 rounded-full font-medium']">
                    {{ Math.round(profileDataProgress) }}%
                  </span>
                </div>
              </button>

              <!-- Opción: Preferencias -->
              <button
                @click="activeSection = 'preferences'"
                :class="[
                  'w-full flex items-center justify-between p-4 rounded-xl transition-all duration-200',
                  activeSection === 'preferences' 
                    ? 'bg-gray-900 text-white shadow-lg' 
                    : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300'
                ]"
              >
                <div class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-3" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clip-rule="evenodd" />
                  </svg>
                  <span class="font-medium">{{ t('userPanel.navigation.preferences') }}</span>
                </div>
                <div class="flex items-center">
                  <span :class="[activeSection === 'preferences' ? 'bg-white/20' : 'bg-gray-100 text-gray-700', 'text-xs px-2 py-1 rounded-full font-medium']">
                    {{ Math.round(preferencesProgress) }}%
                  </span>
                </div>
              </button>

              <!-- Opción: Documentación -->
              <button
                @click="activeSection = 'documents'"
                :class="[
                  'w-full flex items-center justify-between p-4 rounded-xl transition-all duration-200',
                  activeSection === 'documents' 
                    ? 'bg-gray-900 text-white shadow-lg' 
                    : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300'
                ]"
              >
                <div class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-3" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clip-rule="evenodd" />
                  </svg>
                  <span class="font-medium">{{ t('userPanel.navigation.documents') }}</span>
                </div>
                <div class="flex items-center">
                  <span :class="[activeSection === 'documents' ? 'bg-white/20' : 'bg-gray-100 text-gray-700', 'text-xs px-2 py-1 rounded-full font-medium']">
                    {{ Math.round(essentialDocumentsProgress) }}%
                  </span>
                </div>
              </button>

              <!-- Opción: Avalista -->
              <button
                @click="activeSection = 'guarantor'"
                :class="[
                  'w-full flex items-center justify-between p-4 rounded-xl transition-all duration-200',
                  activeSection === 'guarantor' 
                    ? 'bg-gray-900 text-white shadow-lg' 
                    : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300'
                ]"
              >
                <div class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-3" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                  </svg>
                  <span class="font-medium">{{ t('userPanel.navigation.guarantor') }}</span>
                </div>
                <div class="flex items-center">
                  <span :class="[
                    activeSection === 'guarantor' ? 'bg-white/20' : hasGuarantor ? 'bg-gray-100 text-gray-700' : 'bg-gray-200 text-gray-600',
                    'text-xs px-2 py-1 rounded-full font-medium'
                  ]">
                    {{ hasGuarantor ? (guarantorInfo.status === 'verified' ? '✓' : '⏳') : '❌' }}
                  </span>
                </div>
              </button>

              <!-- Opción: Mis Pagos -->
              <button
                @click="activeSection = 'payments'"
                :class="[
                  'w-full flex items-center justify-between p-4 rounded-xl transition-all duration-200',
                  activeSection === 'payments' 
                    ? 'bg-gray-900 text-white shadow-lg' 
                    : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300'
                ]"
              >
                <div class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-3" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                    <path fill-rule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clip-rule="evenodd" />
                  </svg>
                  <span class="font-medium">{{ t('userPanel.navigation.payments') }}</span>
                </div>
                <div class="flex items-center">
                  <span :class="[
                    activeSection === 'payments' ? 'bg-white/20' : totalPaymentsMade > 0 ? 'bg-gray-100 text-gray-700' : 'bg-gray-200 text-gray-600',
                    'text-xs px-2 py-1 rounded-full font-medium'
                  ]">
                    {{ totalPaymentsMade }}
                  </span>
                </div>
              </button>
            </nav>

            <!-- Consejos -->
            <div class="mt-6 p-4 rounded-xl bg-white border border-gray-300">
              <h4 class="text-sm font-bold text-gray-900 mb-3">{{ t('userPanel.tips.title') }}</h4>
              <div class="space-y-2 text-xs text-gray-700">
                <p>{{ t('userPanel.tips.uploadDocuments') }}</p>
                <p>{{ t('userPanel.tips.addDescription') }}</p>
                <p>{{ t('userPanel.tips.addGuarantor') }}</p>
              </div>
            </div>

            <!-- Enlace volver -->
            <div class="mt-6">
              <router-link 
                to="/contracts"
                class="flex items-center justify-center w-full p-3 text-sm font-medium text-gray-900 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-all duration-200"
              >
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
                </svg>
                {{ t('userPanel.sidebar.backToHome') }}
              </router-link>
            </div>
            </div>
            
          <!-- Footer del sidebar -->
          <div class="p-4 border-t border-gray-200">
            <div class="flex justify-center items-center space-x-2 text-xs text-gray-500">
              <router-link to="/privacy-policy" class="hover:text-gray-700">{{ t('userPanel.footer.privacy') }}</router-link>
              <span>|</span>
              <router-link to="/contacto" class="hover:text-gray-700">{{ t('userPanel.footer.contact') }}</router-link>
            </div>
          </div>
        </div>

        <!-- CONTENIDO PRINCIPAL -->
        <div :class="showSidebar ? 'lg:ml-80' : ''" class="flex-1 min-w-0 w-full">
          
          <!-- Botón para mostrar sidebar (cuando está oculto) -->
          <div v-if="!showSidebar" class="hidden lg:flex fixed top-24 left-4 z-20">
            <button
              @click="showSidebar = true"
              class="p-2.5 bg-white text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 hover:shadow-md shadow-sm transition-all duration-200"
              title="Abrir panel lateral"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clip-rule="evenodd" />
                  </svg>
            </button>
          </div>
          <!-- Banner de perfil incompleto (solo después de cargar datos) -->
          <div 
            v-if="isInitialDataLoaded && isProfileIncomplete && missingFields.length > 0" 
            class="px-4 sm:px-6 lg:px-8 py-4"
          >
            <div>
              <div class="flex items-start justify-between">
                <div class="flex-1">
                  <div class="flex items-center mb-2">
                    <h3 class="text-lg font-semibold text-indigo-900">
                      {{ authProvider === 'google' ? t('userPanel.banner.titleGoogle') : t('userPanel.banner.title') }}
                    </h3>
                  </div>
                  
                  <p class="text-sm text-indigo-800 mb-3">
                    {{ t('userPanel.banner.description') }}
                  </p>
                  
                  <!-- Lista de campos faltantes (máximo 5) -->
                  <div v-if="missingFields.length > 0" class="space-y-1 mb-3">
                    <p class="text-sm font-medium text-indigo-900 mb-2">
                      {{ t('userPanel.banner.missingFieldsLabel') }}
                    </p>
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div 
                        v-for="(field, index) in missingFields.slice(0, 5)" 
                        :key="index"
                        class="flex items-center text-sm text-indigo-700"
                      >
                        <svg class="w-4 h-4 mr-2 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                        </svg>
                        {{ field }}
                      </div>
                    </div>
                    <p v-if="missingFields.length > 5" class="text-xs text-indigo-600 mt-2">
                      {{ t('userPanel.banner.andMore', { count: missingFields.length - 5 }) }}
                    </p>
                  </div>
                  
                  <!-- Botones de acción -->
                  <div class="flex flex-wrap gap-2">
                    <button
                      @click="activeSection = 'profile'"
                      class="inline-flex items-center px-4 py-2 bg-white text-indigo-600 border border-indigo-200 text-sm font-medium rounded-full hover:bg-indigo-50 hover:shadow-md shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                      {{ t('userPanel.banner.completeNowButton') }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Header del contenido -->
          <div :class="[
            'px-4 sm:px-6 lg:px-8 py-4',
            !showSidebar ? 'lg:pl-16' : ''
          ]">
            <div class="flex items-center justify-between mb-4">
              <div>
                <h2 class="text-2xl font-bold text-gray-900">
                  {{ getSectionTitle() }}
                </h2>
                <p class="text-sm text-gray-500 mt-1">
                  {{ getSectionDescription() }}
                </p>
              </div>
              
              <!-- Progreso de la sección actual (desktop) -->
              <div class="hidden md:flex items-center space-x-4">
                <div class="text-right">
                  <div class="text-sm font-semibold text-gray-700">{{ getSectionProgressLabel() }}</div>
                  <div class="text-xs text-gray-500">{{ Math.round(getSectionProgress()) }}%</div>
                </div>
                <div class="w-24 h-2 bg-gray-200 rounded-full">
                  <div 
                    class="h-2 bg-gray-900 rounded-full transition-all duration-300"
                    :style="{ width: `${getSectionProgress()}%` }"
                  ></div>
          </div>
        </div>
      </div>

            <!-- Navegación móvil (tabs) -->
            <div class="lg:hidden mb-6 px-4">
              <div class="flex space-x-2 overflow-x-auto pb-2 -mx-4 px-4 no-scrollbar">
                  <button
                  @click="activeSection = 'profile'"
                    :class="[
                    'flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200',
                    activeSection === 'profile' 
                      ? 'bg-black text-white' 
                      : 'bg-white border border-gray-200 text-gray-600'
                  ]"
                >
                  Perfil
                </button>
                <button
                  @click="activeSection = 'preferences'"
                  :class="[
                    'flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200',
                    activeSection === 'preferences' 
                      ? 'bg-black text-white' 
                      : 'bg-white border border-gray-200 text-gray-600'
                  ]"
                >
                  Preferencias
                </button>
                <button
                  @click="activeSection = 'documents'"
                  :class="[
                    'flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200',
                    activeSection === 'documents' 
                      ? 'bg-black text-white' 
                      : 'bg-white border border-gray-200 text-gray-600'
                    ]"
                  >
                  Documentos
                </button>
                <button
                  @click="activeSection = 'guarantor'"
                  :class="[
                    'flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200',
                    activeSection === 'guarantor' 
                      ? 'bg-black text-white' 
                      : 'bg-white border border-gray-200 text-gray-600'
                  ]"
                >
                  Avalista
                </button>
                <button
                  @click="activeSection = 'payments'"
                  :class="[
                    'flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200',
                    activeSection === 'payments' 
                      ? 'bg-black text-white' 
                      : 'bg-white border border-gray-200 text-gray-600'
                  ]"
                >
                  Pagos
                </button>
                </div>
              </div>

            <!-- ==================== SECCIÓN: DOCUMENTOS ==================== -->
            <div v-if="activeSection === 'documents'" class="max-w-2xl mx-auto pb-24 lg:pb-8 px-4 sm:px-0">
              <div class="p-3 sm:p-6">
                <h3 class="text-lg font-bold text-gray-900 mb-2">Documentación</h3>
                <p class="text-sm text-gray-600 mb-6">
                  Sube la documentación necesaria para poder ser seleccionado por un propietario
                  </p>

                

                  <!-- Progreso de documentos -->
                  <div class="mt-2 p-3 sm:p-4 mb-4 min-w-0">
                    <div class="flex items-center justify-between mb-2 min-w-0">
                      <span class="text-sm font-medium text-gray-700 truncate pr-2">Progreso de Documentos</span>
                      <span class="text-sm font-bold text-gray-900 flex-shrink-0">{{ Math.round(essentialDocumentsProgress) }}%</span>
                    </div>
                    <div class="w-full bg-gray-200 rounded-full h-3 min-w-0">
                      <div 
                        class="bg-gray-900 h-3 rounded-full transition-all duration-300"
                        :style="{ width: `${essentialDocumentsProgress}%` }"
                      ></div>
                    </div>
                    <p class="text-sm text-gray-600 mt-2 break-words">
                      {{ getCompletedEssentialDocuments() }} de {{ essentialDocuments.length }} documentos completados
                    </p>
                  </div>
                  
                  <!-- Lista de documentos esenciales filtrada -->
                  <div v-if="filteredEssentialDocuments.length > 0" class="space-y-3 min-w-0">
                    <div 
                      v-for="docType in filteredEssentialDocuments" 
                      :key="docType.key"
                      class="p-3 sm:p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors min-w-0"
                    >
                      <!-- Información del documento -->
                      <div class="min-w-0 mb-3">
                        <div class="flex items-start space-x-3 min-w-0">
                          <!-- Información del documento -->
                          <div class="min-w-0 flex-1">
                            <span class="text-sm sm:text-base font-semibold text-gray-900 block break-words">{{ docType.name }}</span>
                            <p v-if="docType.description" class="text-xs sm:text-sm text-gray-500 mt-1 break-words leading-relaxed">{{ docType.description }}</p>
                            
                            <!-- Estado del documento -->
                            <div class="flex flex-col sm:flex-row sm:items-center mt-2 gap-1 sm:gap-3">
                              <span v-if="getDocumentStatus(docType.key) === 'uploaded'" class="text-xs sm:text-sm text-green-600 font-medium flex-shrink-0">
                                ✓ Subido correctamente
                              </span>
                              <span v-else-if="getDocumentStatus(docType.key) === 'uploading'" class="text-xs sm:text-sm text-blue-600 font-medium flex-shrink-0">
                                Subiendo...
                              </span>
                              <span v-else class="text-xs sm:text-sm text-amber-600 font-medium flex-shrink-0">
                                Pendiente de subir
                              </span>
                              
                              <!-- Contador de archivos subidos -->
                              <span v-if="getUploadedFilesCount(docType.key) > 0" class="text-xs sm:text-sm text-gray-500 flex-shrink-0">
                                ({{ getUploadedFilesCount(docType.key) }} archivo{{ getUploadedFilesCount(docType.key) > 1 ? 's' : '' }})
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <!-- Botones de acción - Siempre debajo -->
                      <div class="flex items-center justify-center sm:justify-start space-x-2 sm:space-x-3 pt-2 border-t border-gray-200">
                        <!-- Input de archivo oculto -->
                        <input 
                          :id="`essential-file-${docType.key}`"
                          type="file" 
                          @change="(event) => handleEssentialDocumentUpload(docType.key, event)"
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" 
                          class="hidden" 
                          multiple
                        >
                        
                        <!-- Botón de subida -->
                        <button 
                          @click="triggerEssentialFileInput(docType.key)"
                          :disabled="isUploadingDocument(docType.key)"
                          class="flex-1 sm:flex-none px-4 py-2.5 text-sm font-medium text-indigo-600 bg-white border border-indigo-200 hover:bg-indigo-50 hover:shadow-md shadow-sm disabled:opacity-50 disabled:cursor-not-allowed rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 flex items-center justify-center gap-2"
                        >
                          <svg v-if="isUploadingDocument(docType.key)" class="animate-spin h-4 w-4 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                          {{ getDocumentStatus(docType.key) === 'uploaded' ? 'Cambiar' : 'Subir' }}
                        </button>
                        
                        <!-- Botón de ver archivos subidos -->
                        <button 
                          v-if="getUploadedFilesCount(docType.key) > 0"
                          @click="showUploadedFiles(docType.key)"
                          class="flex-1 sm:flex-none px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 flex items-center justify-center gap-2"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                            <path fill-rule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clip-rule="evenodd" />
                          </svg>
                          Ver
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Estado cuando no hay documentos que mostrar -->
                  <div v-else class="text-center py-6 sm:py-8 min-w-0">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-10 w-10 sm:h-12 sm:w-12 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <h3 class="text-base sm:text-lg font-bold text-[#0e141b] mb-2 break-words">
                      {{ activeFilter === 'pending' ? 'No hay documentos pendientes' : 'No hay documentos completados' }}
                    </h3>
                    <p class="text-sm sm:text-base text-gray-600 mb-4 break-words leading-relaxed">
                      {{ activeFilter === 'pending' 
                        ? '¡Excelente! Todos los documentos esenciales han sido subidos.' 
                        : 'Aún no has subido ningún documento esencial.' 
                      }}
                    </p>
                  </div>
              </div>
            </div>
            <!-- FIN SECCIÓN: DOCUMENTOS -->

            <!-- ==================== SECCIÓN: PERFIL ==================== -->
            <div v-if="activeSection === 'profile'" class="max-w-2xl mx-auto pb-24 lg:pb-8 px-4 sm:px-0">
              <div class="sm:p-6 ">
                  
                  <form @submit.prevent="updateProfile" class="space-y-6">
                    
                    <!-- SECCIÓN 1: DATOS PERSONALES BÁSICOS -->
                    <div class="pb-4 border-b border-gray-200">
                      <button type="button" @click="toggleSection('personalData')" class="w-full flex items-center justify-between text-left">
                        <h4 class="text-sm font-semibold text-gray-900 flex items-center">
                          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-indigo-600" viewBox="0 0 20 20" fill="currentColor">
                            <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd" />
                          </svg>
                          {{ t('userPanel.profile.personalData.title') }}
                          <span v-if="personalDataProgress === 100" class="ml-2 text-xs text-green-600">✓</span>
                        </h4>
                        <svg :class="['h-5 w-5 text-gray-500 transition-transform', expandedSections.personalData ? 'rotate-180' : '']" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                        </svg>
                      </button>
                      
                      <div v-show="expandedSections.personalData" class="mt-4 space-y-4">
                        <!-- Nombre completo -->
                        <div>
                          <label for="fullName" class="block text-sm font-medium text-gray-700 mb-2">{{ t('userPanel.profile.personalData.fullName') }}</label>
                          <input v-model="fullName" type="text" id="fullName" required
                            class="w-full border border-gray-300 rounded-lg shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-900"
                            :placeholder="t('userPanel.profile.personalData.fullNamePlaceholder')">
                    </div>
                        
                        <!-- Teléfono -->
                        <div>
                          <label for="phone" class="block text-sm font-medium text-gray-700 mb-2">{{ t('userPanel.profile.personalData.phone') }}</label>
                          <input :value="phone" @input="formatPhone" type="tel" id="phone" required maxlength="11"
                            class="w-full border border-gray-300 rounded-lg shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-900"
                            :placeholder="t('userPanel.profile.personalData.phonePlaceholder')">
                    </div>
                        
                        <!-- Email (solo lectura) -->
                      <div>
                          <label class="block text-sm font-medium text-gray-700 mb-2">{{ t('userPanel.profile.personalData.email') }}</label>
                          <div class="flex items-center">
                            <input :value="email" type="email" disabled
                              class="flex-1 border border-gray-200 bg-gray-50 rounded-lg shadow-sm py-3 px-4 text-gray-600">
                            <span class="ml-2 text-xs text-green-600">✓</span>
                      </div>
                    </div>
                      </div>
                    </div>
                    
                    <!-- SECCIÓN 2: IDENTIFICACIÓN -->
                    <div class="pb-4 border-b border-gray-200">
                      <button type="button" @click="toggleSection('identification')" class="w-full flex items-center justify-between text-left">
                        <h4 class="text-sm font-semibold text-gray-900 flex items-center">
                          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-indigo-600" viewBox="0 0 20 20" fill="currentColor">
                            <path fill-rule="evenodd" d="M10 2a1 1 0 00-1 1v1a1 1 0 002 0V3a1 1 0 00-1-1zM4 4h3a3 3 0 006 0h3a2 2 0 012 2v9a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2zm2.5 7a1.5 1.5 0 100-3 1.5 1.5 0 000 3zm2.45 4a2.5 2.5 0 10-4.9 0h4.9zM12 9a1 1 0 100 2h3a1 1 0 100-2h-3zm-1 4a1 1 0 011-1h2a1 1 0 110 2h-2a1 1 0 01-1-1z" clip-rule="evenodd" />
                          </svg>
                          {{ t('userPanel.profile.identification.title') }}
                          <span v-if="identificationProgress === 100" class="ml-2 text-xs text-green-600">✓</span>
                          <span v-else class="ml-2 text-xs text-amber-600">{{ t('userPanel.profile.identification.requiredForSigning') }}</span>
                        </h4>
                        <svg :class="['h-5 w-5 text-gray-500 transition-transform', expandedSections.identification ? 'rotate-180' : '']" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                        </svg>
                      </button>
                      
                      <div v-show="expandedSections.identification" class="mt-4 space-y-4">
                        <!-- Selector de País -->
                        <div>
                          <label for="documentCountry" class="block text-sm font-medium text-gray-700 mb-2">
                            {{ t('userPanel.document.selectCountry') }} *
                          </label>
                          <select 
                            v-model="documentCountry" 
                            @change="handleCountryChange"
                            id="documentCountry"
                            class="w-full border border-gray-300 rounded-lg shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-900"
                          >
                            <option value="" disabled>{{ t('userPanel.document.selectCountryPlaceholder') }}</option>
                            <optgroup v-for="(countries, region) in countriesByRegion" :key="region" :label="region">
                              <option v-for="country in countries" :key="country.code" :value="country.code">
                                {{ country.flag }} {{ country.name }}
                              </option>
                            </optgroup>
                          </select>
                        </div>

                        <!-- Selector de Tipo de Documento (SOLO para NO españoles) -->
                        <div v-if="documentCountry !== 'ES' && availableDocumentTypes.length > 0">
                          <label for="documentType" class="block text-sm font-medium text-gray-700 mb-2">
                            {{ t('userPanel.document.selectType') }} *
                          </label>
                          <div class="grid grid-cols-1 gap-3">
                            <label 
                              v-for="docType in availableDocumentTypes" 
                              :key="docType.value"
                              class="relative flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all hover:border-indigo-300"
                              :class="documentType === docType.value ? 'border-indigo-600 bg-indigo-50' : 'border-gray-200'"
                            >
                              <input 
                                type="radio" 
                                :value="docType.value" 
                                v-model="documentType"
                                @change="handleDocumentTypeChange"
                                class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                              />
                              <div class="ml-3 flex-1">
                                <div class="flex items-center">
                                  <span class="text-xl mr-2">{{ docType.icon }}</span>
                                  <span class="font-medium text-gray-900">{{ docType.label }}</span>
                                </div>
                                <p class="text-xs text-gray-500 mt-1">{{ docType.description }}</p>
                              </div>
                            </label>
                          </div>
                        </div>

                        <!-- Número de Documento -->
                        <div>
                          <label for="documentNumber" class="block text-sm font-medium text-gray-700 mb-2">
                            <template v-if="documentCountry === 'ES'">
                              DNI o NIE *
                            </template>
                            <template v-else>
                              {{ currentDocumentConfig.label }} *
                            </template>
                          </label>
                          <input 
                            v-model="documentNumber"
                            @input="handleDocumentNumberInput" 
                            type="text" 
                            id="documentNumber"
                            :class="['w-full border rounded-lg shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-900 uppercase',
                              documentError ? 'border-red-500' : 'border-gray-300']"
                            :placeholder="documentCountry === 'ES' ? '12345678Z o X1234567L' : currentDocumentConfig.placeholder"
                            :maxlength="documentCountry === 'ES' ? 9 : currentDocumentConfig.maxLength"
                          />
                          <p v-if="documentError" class="mt-1 text-sm text-red-600">{{ documentError }}</p>
                          <p v-if="documentValidationMessage" class="mt-1 text-sm text-green-600">{{ documentValidationMessage }}</p>
                          <p class="mt-1 text-xs text-gray-500">
                            <template v-if="documentCountry === 'ES'">
                              Introduce tu DNI (ej: 12345678Z) o NIE (ej: X1234567L)
                            </template>
                            <template v-else>
                              {{ currentDocumentConfig.helpText }}
                            </template>
                          </p>
                        </div>
                        
                        <!-- Fotos del Documento (SOLO PARA ESPAÑOLES CON DNI/NIE) -->
                        <div v-if="requiresOcrVerification" class="space-y-4">
                          <label class="block text-sm font-medium text-gray-700">
                            {{ t('userPanel.document.uploadPhotos') }} *
                          </label>

                          <!-- Frente del documento -->
                          <div>
                            <label class="block text-xs font-medium text-gray-600 mb-2">
                              {{ t('userPanel.document.frontSide') }}
                            </label>
                            <input id="document-front-input" type="file" @change="handleDocumentFrontUpload" accept="image/*" class="hidden">
                            
                            <div v-if="documentFrontUrl" class="mb-3">
                              <img :src="documentFrontUrl" alt="Frente del documento" class="h-32 w-auto rounded-lg border object-cover">
                              
                              <!-- Estado OCR -->
                              <div class="mt-2 flex items-center">
                                <template v-if="ocrVerificationStatus.front.status === 'verifying'">
                                  <svg class="animate-spin h-4 w-4 text-blue-600 mr-2" fill="none" viewBox="0 0 24 24">
                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                  <span class="text-sm text-blue-600">Verificando...</span>
                                </template>
                                <template v-else-if="ocrVerificationStatus.front.status === 'verified'">
                                  <svg class="h-4 w-4 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                  </svg>
                                  <span class="text-sm text-green-600">DNI verificado</span>
                                </template>
                                <template v-else-if="ocrVerificationStatus.front.status === 'failed'">
                                  <svg class="h-4 w-4 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                  </svg>
                                  <span class="text-sm text-red-600">{{ ocrVerificationStatus.front.message }}</span>
                                  <button type="button" @click="retryOcrVerification('front')" class="ml-2 text-xs text-indigo-600 underline">Reintentar</button>
                                </template>
                              </div>
                            </div>
                            
                            <button type="button" @click="triggerDocumentFrontInput"
                              class="w-full py-3 px-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-indigo-500 hover:text-indigo-600 transition-colors flex items-center justify-center">
                              <svg class="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                              </svg>
                              {{ documentFrontUrl ? 'Cambiar foto' : 'Subir foto del DNI' }}
                            </button>
                          </div>
                        </div>
                        
                        <!-- Mensaje informativo para usuarios no españoles -->
                        <div v-else class="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                          <div class="flex items-start">
                            <svg class="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                            </svg>
                            <div>
                              <p class="text-sm font-medium text-blue-900">Solo necesitamos el número de tu documento</p>
                              <p class="text-xs text-blue-700 mt-1">
                                Para documentos internacionales, solo es necesario que introduzcas el número. No necesitas subir fotos.
                              </p>
                            </div>
                          </div>
                        </div>
                        
                        <!-- Dirección habitual -->
                      <div>
                          <label for="address" class="block text-sm font-medium text-gray-700 mb-2">{{ t('userPanel.profile.identification.address') }}</label>
                          <input v-model="address" type="text" id="address"
                            class="w-full border border-gray-300 rounded-lg shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-900"
                            :placeholder="t('userPanel.profile.identification.addressPlaceholder')">
                          <p class="mt-1 text-xs text-gray-500">{{ t('userPanel.profile.identification.addressNote') }}</p>
                      </div>
                        
                        <!-- ¿Con otra persona? -->
                        <div class="pt-3 border-t border-gray-100">
                          <label class="flex items-center cursor-pointer">
                            <input type="checkbox" v-model="withOtherPerson" class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded">
                            <span class="ml-3 text-sm font-medium text-gray-700">{{ t('userPanel.profile.identification.withOtherPerson') }}</span>
                          </label>
                          
                          <div v-if="withOtherPerson" class="mt-4 space-y-4 pl-7">
                            <div>
                              <label for="otherPersonName" class="block text-sm font-medium text-gray-700 mb-2">{{ t('userPanel.profile.identification.companionName') }}</label>
                              <input v-model="otherPersonName" type="text" id="otherPersonName"
                                class="w-full border border-gray-300 rounded-lg shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-900"
                                :placeholder="t('userPanel.profile.identification.companionNamePlaceholder')">
                            </div>
                            <div>
                              <label for="otherPersonDni" class="block text-sm font-medium text-gray-700 mb-2">{{ t('userPanel.profile.identification.companionDni') }}</label>
                              <input v-model="otherPersonDni" type="text" id="otherPersonDni"
                                class="w-full border border-gray-300 rounded-lg shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-900 uppercase"
                                :placeholder="t('userPanel.profile.identification.companionDniPlaceholder')" maxlength="9">
                    </div>
                  </div>
              </div>
            </div>
          </div>

                    <!-- Botón de actualizar -->
                    <div class="pt-4">
                      <button 
                        type="submit" 
                        :disabled="isUpdatingProfile"
                        class="w-full flex justify-center items-center py-3 px-4 bg-white text-indigo-600 border border-indigo-200 rounded-full hover:bg-indigo-50 hover:shadow-md shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                      >
                        <svg v-if="isUpdatingProfile" class="animate-spin -ml-1 mr-3 h-5 w-5 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {{ isUpdatingProfile ? t('userPanel.profile.actions.updating') : t('userPanel.profile.actions.update') }}
                      </button>
                    </div>
                  </form>
                  
                  <!-- Progreso del perfil -->
                  <div class="mt-6 p-4 bg-gray-50 rounded-lg">
                    <div class="flex items-center justify-between mb-2">
                      <span class="text-sm font-medium text-gray-700">{{ t('userPanel.profile.progress.title') }}</span>
                      <span class="text-sm font-bold text-indigo-600">{{ Math.round(profileDataProgress) }}%</span>
                    </div>
                    <div class="w-full bg-gray-200 rounded-full h-3">
                      <div 
                        class="bg-gradient-to-r from-green-500 to-emerald-600 h-3 rounded-full transition-all duration-300"
                        :style="{ width: `${profileDataProgress}%` }"
                      ></div>
                    </div>
                    <p class="text-sm text-gray-600 mt-2">
                      {{ t('userPanel.profile.progress.fieldsCompleted', { completed: getCompletedProfileFields() }) }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <!-- FIN SECCIÓN: PERFIL -->

            <!-- ==================== SECCIÓN: PREFERENCIAS ==================== -->
            <div v-if="activeSection === 'preferences'" class="max-w-2xl mx-auto pb-24 lg:pb-8 px-4 sm:px-0">
              <div class="sm:p-6 ">
                <h3 class="text-lg font-bold text-gray-900 mb-2">{{ t('userPanel.sections.preferences.title') }}</h3>
                <p class="text-sm text-gray-600 mb-6">
                  {{ t('userPanel.sections.preferences.description') }}
                </p>

                <form @submit.prevent="updateProfile" class="space-y-6">
                  <!-- Preferencias de búsqueda de coliving -->
                  <div class="pb-4 border-b border-gray-200">
                    <h4 class="text-sm font-semibold text-gray-900 mb-4 flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-indigo-600" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd" />
                      </svg>
                      {{ t('userPanel.profile.searchPreferences.title') }}
                    </h4>
                    <p class="text-xs text-gray-500 mb-4">
                      {{ t('userPanel.profile.searchPreferences.subtitle') }}
                    </p>
                    
                    <!-- Ciudad preferida -->
                    <div class="mb-4">
                      <label for="preferredCity" class="block text-sm font-medium text-gray-700 mb-2">
                        {{ t('userPanel.profile.searchPreferences.preferredCity') }}
                      </label>
                      <select
                        v-model="preferredCity"
                        id="preferredCity"
                        class="w-full border border-gray-300 rounded-lg shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-900 bg-white"
                      >
                        <option value="">{{ t('userPanel.profile.searchPreferences.noPreference') }}</option>
                        <option v-for="city in availableCities" :key="city" :value="city">
                          {{ city }}
                        </option>
                      </select>
                      <p class="text-xs text-gray-500 mt-1">
                        {{ t('userPanel.profile.searchPreferences.cityNote') }}
                      </p>
                    </div>

                    <!-- ¿Eres estudiante? -->
                    <div>
                      <label class="flex items-center cursor-pointer group">
                        <div class="relative">
                          <input
                            type="checkbox"
                            v-model="isStudent"
                            class="sr-only peer"
                          >
                          <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                        </div>
                        <span class="ml-3 text-sm font-medium text-gray-700 group-hover:text-gray-900">
                          {{ t('userPanel.profile.searchPreferences.isStudent') }}
                        </span>
                      </label>
                      <p class="text-xs text-gray-500 mt-2 ml-14">
                        {{ t('userPanel.profile.searchPreferences.studentNote') }}
                      </p>
                    </div>
                  </div>
                  
                  <!-- Descripción del candidato -->
                  <div class="pb-4 border-b border-gray-200">
                    <label for="applicantDescription" class="block text-sm font-medium text-gray-700 mb-2">
                      {{ t('userPanel.profile.description.title') }}
                    </label>
                    <textarea 
                      v-model="applicantDescription" 
                      id="applicantDescription" 
                      rows="4" 
                      maxlength="500"
                      class="w-full border border-gray-300 rounded-lg shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-900 resize-none"
                      :placeholder="t('userPanel.profile.description.placeholder')"
                    ></textarea>
                    <div class="flex items-center justify-between mt-2">
                      <p class="text-sm text-gray-500">
                        {{ applicantDescription ? applicantDescription.length : 0 }}/500 {{ t('userPanel.profile.description.characters') }}
                      </p>
                      <div class="flex items-center">
                        <span v-if="applicantDescription && applicantDescription.length > 50" class="text-xs text-green-600 flex items-center">
                          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                          </svg>
                          ¡Buena descripción!
                        </span>
                      </div>
                    </div>
                    <div class="mt-2 text-xs text-gray-500 bg-blue-50 p-3 rounded-lg">
                      <strong>💡 Consejo:</strong> Una descripción detallada y personal aumenta significativamente tus posibilidades de ser seleccionado por los propietarios.
                    </div>
                  </div>

                  <!-- Ingresos mensuales -->
                  <div>
                    <label for="monthlyIncome" class="block text-sm font-medium text-gray-700 mb-2">
                      {{ t('userPanel.profile.income.title') }}
                    </label>
                    <div class="relative">
                      <input 
                        v-model.number="monthlyIncome" 
                        type="number" 
                        id="monthlyIncome"
                        min="0"
                        step="100"
                        class="w-full border border-gray-300 rounded-lg shadow-sm py-3 px-4 pr-12 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-900"
                        :placeholder="t('userPanel.profile.income.placeholder')"
                      >
                      <div class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <span class="text-gray-500 text-sm">€</span>
                      </div>
                    </div>
                    <div class="flex items-center justify-between mt-2">
                      <p class="text-sm text-gray-500">
                        {{ t('userPanel.profile.income.note') }}
                      </p>
                      <div class="flex items-center">
                        <span v-if="monthlyIncome && monthlyIncome >= 1000" class="text-xs text-green-600 flex items-center">
                          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                          </svg>
                          {{ t('userPanel.profile.income.competitiveRange') }}
                        </span>
                      </div>
                    </div>
                    <div class="mt-2 text-xs text-gray-500 bg-blue-50 p-3 rounded-lg">
                      <strong>{{ t('userPanel.profile.income.privacyNote') }}</strong>
                    </div>
                  </div>

                  <!-- Botón de actualizar -->
                  <div class="pt-4">
                    <button 
                      type="submit" 
                      :disabled="isUpdatingProfile"
                      class="w-full flex justify-center items-center py-3 px-4 bg-white text-indigo-600 border border-indigo-200 rounded-full hover:bg-indigo-50 hover:shadow-md shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                    >
                      <svg v-if="isUpdatingProfile" class="animate-spin -ml-1 mr-3 h-5 w-5 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {{ isUpdatingProfile ? t('userPanel.profile.actions.updating') : t('userPanel.profile.actions.update') }}
                    </button>
                  </div>
                </form>

                <!-- Progreso de preferencias -->
                <div class="mt-6 p-4 bg-gray-50 rounded-lg">
                  <div class="flex items-center justify-between mb-2">
                    <span class="text-sm font-medium text-gray-700">{{ t('userPanel.sections.preferences.progressLabel') }}</span>
                    <span class="text-sm font-bold text-purple-600">{{ Math.round(preferencesProgress) }}%</span>
                  </div>
                  <div class="w-full bg-gray-200 rounded-full h-3">
                    <div 
                      class="bg-gradient-to-r from-purple-500 to-indigo-600 h-3 rounded-full transition-all duration-300"
                      :style="{ width: `${preferencesProgress}%` }"
                    ></div>
                  </div>
                  <p class="text-sm text-gray-600 mt-2">
                    {{ getCompletedPreferencesFields() }} de 2 campos completados
                  </p>
                </div>

                <!-- Eliminar cuenta -->
                <div class="mt-6 border border-red-200 rounded-lg p-4 bg-red-50/40">
                  <h4 class="text-sm font-semibold text-red-800">{{ t('userPanel.accountDeletion.title') }}</h4>
                  <p class="mt-1 text-sm text-red-700">{{ t('userPanel.accountDeletion.description') }}</p>

                  <div v-if="isLoadingDeleteEligibility" class="mt-3 text-sm text-gray-600">
                    {{ t('userPanel.accountDeletion.checking') }}
                  </div>

                  <div v-else-if="deleteAccountEligibility && !deleteAccountEligibility.canDelete" class="mt-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                    {{ deleteAccountEligibility.reason || t('userPanel.accountDeletion.blockedDefault') }}
                  </div>

                  <div v-else-if="deleteAccountEligibility?.canDelete" class="mt-4 space-y-3">
                    <p class="text-xs text-gray-600">{{ t('userPanel.accountDeletion.warning') }}</p>
                    <label class="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        v-model="deleteAccountConfirmed"
                        type="checkbox"
                        class="mt-1 h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                      />
                      <span>{{ t('userPanel.accountDeletion.confirmLabel') }}</span>
                    </label>
                    <button
                      type="button"
                      @click="handleDeleteAccount"
                      :disabled="!deleteAccountConfirmed || isDeletingAccount"
                      class="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {{ isDeletingAccount ? t('userPanel.accountDeletion.deleting') : t('userPanel.accountDeletion.button') }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <!-- FIN SECCIÓN: PREFERENCIAS -->

            <!-- ==================== SECCIÓN: MIS PAGOS ==================== -->
            <div v-if="activeSection === 'payments'">
              <UserPayments :current-user="currentUser" />
            </div>
            <!-- FIN SECCIÓN: MIS PAGOS -->

            <!-- ==================== SECCIÓN: AVALISTA ==================== -->
            <div v-if="activeSection === 'guarantor'" class="max-w-2xl mx-auto pb-24 lg:pb-8 px-4 sm:px-0">
              
            
            <!-- Gestión del Avalista - Card principal -->
            <div class="p-6">
                    <div class="flex items-start space-x-3">
                <div class="flex-1">
                  <h3 class="text-xl font-bold text-[#0e141b] mb-2">Gestión de Avalista</h3>
                  <p class="text-sm text-gray-700 mb-4">
                    Añade un avalista para fortalecer tu candidatura y dar mayor confianza a los propietarios
                  </p>

                  <!-- Estado sin avalista -->
                  <div v-if="!hasGuarantor" class="space-y-4">
                    <div >
                      <div class="flex items-start">
                        
                      <div>
                          <h4 class="text-sm font-medium text-gray-800 mb-2">Ventajas de tener avalista:</h4>
                          <ul class="text-sm text-gray-700 space-y-1">
                            <li class="flex items-center">
                              <span class="text-green-600 mr-2">✓</span>
                              Mayor confianza de los propietarios
                            </li>
                            <li class="flex items-center">
                              <span class="text-green-600 mr-2">✓</span>
                              Perfil más competitivo
                            </li>
                            <li class="flex items-center">
                              <span class="text-green-600 mr-2">✓</span>
                              Te diferencias de otros candidatos
                            </li>
                            <li class="flex items-center">
                              <span class="text-green-600 mr-2">✓</span>
                              Seguridad adicional garantizada
                            </li>
                          </ul>
                      </div>
                    </div>
                  </div>

                    <button 
                      @click="openGuarantorModal"
                      class="w-full flex justify-center items-center py-4 px-6 bg-white text-indigo-600 border border-indigo-200 rounded-full text-base font-semibold hover:bg-indigo-50 hover:shadow-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Añadir Avalista Ahora
                    </button>
                </div>

                  <!-- Estado con avalista -->
                  <div v-else class="space-y-4">
                    <!-- Información del avalista -->
                    <div v-if="guarantorInfo && guarantorInfo.fullName" >
                      <div class="flex items-center justify-between mb-4">
                        <h4 class="text-lg font-semibold text-emerald-800 flex items-center">
                          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Avalista Configurado
                        </h4>
                        <span class="text-xs bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full font-medium">
                          Activo
                        </span>
              </div>
                      
                      <div class="grid grid-cols-1 gap-3 text-sm">
                        <div class="flex justify-between">
                          <span class="font-medium text-gray-700">Nombre:</span>
                          <span class="text-gray-900 font-semibold">{{ guarantorInfo.fullName }}</span>
            </div>
                        <div class="flex justify-between">
                          <span class="font-medium text-gray-700">DNI:</span>
                          <span class="text-gray-900">{{ guarantorInfo.dni }}</span>
          </div>
                        <div class="flex justify-between">
                          <span class="font-medium text-gray-700">Teléfono:</span>
                          <span class="text-gray-900">{{ guarantorInfo.phone }}</span>
                        </div>
                        <div class="flex justify-between">
                          <span class="font-medium text-gray-700">Email:</span>
                          <span class="text-gray-900 truncate">{{ guarantorInfo.email }}</span>
                        </div>
                        <div class="flex justify-between">
                          <span class="font-medium text-gray-700">Relación:</span>
                          <span class="text-gray-900">{{ getRelationshipLabel(guarantorInfo.relationshipType) }}</span>
                        </div>
                        <div v-if="guarantorInfo.signedAt" class="flex justify-between">
                          <span class="font-medium text-gray-700">📅 Firmado:</span>
                          <span class="text-gray-900">{{ formatDate(guarantorInfo.signedAt) }}</span>
                        </div>
                      </div>

                      <!-- Estado de verificación -->
                      <div class="mt-4 pt-4 border-t border-emerald-200">
                        <div class="flex items-center justify-between">
                          <div class="flex items-center">
                            <div class="flex items-center text-emerald-700">
                              
                              <span class="text-sm font-medium">
                                {{ guarantorInfo.status === 'verified' ? '🔐 Firmado digitalmente' : 
                                   guarantorInfo.status === 'rejected' ? '❌ Rechazado' :
                                   '⏳ Pendiente de firma' }}
                              </span>
                            </div>
                          </div>
                          <div class="flex items-center space-x-2">
                            <button 
                              @click="refreshDocuments"
                              class="text-xs text-gray-600 hover:text-gray-800 underline font-medium flex items-center"
                              title="Refrescar estado"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                              
                            </button>
                            <button 
                              @click="editGuarantor"
                              class="text-sm text-indigo-600 hover:text-indigo-800 underline font-medium"
                            >
                              ✏️ Editar
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Botón para eliminar avalista -->
                    <div class="flex justify-between items-center pt-4 border-t border-emerald-200">
                      <button 
                        @click="removeGuarantor"
                        class="text-sm text-red-600 hover:text-red-800 underline flex items-center font-medium"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Eliminar Avalista
                      </button>
                    </div>
                  </div>

                  <!-- Progreso del avalista -->
                  <div class="mt-6 p-4">
                    <div class="flex items-center justify-between mb-3">
                      <span class="text-sm font-semibold text-emerald-800">📊 Estado del Avalista</span>
                      <span class="text-lg font-bold text-emerald-600">{{ guarantorProgress }}%</span>
                    </div>
                    <div class="w-full bg-emerald-200 rounded-full h-4">
                      <div 
                        class="h-4 rounded-full transition-all duration-500 relative overflow-hidden"
                        :class="{
                          'bg-gradient-to-r from-emerald-500 to-green-600': guarantorInfo.status !== 'rejected',
                          'bg-gradient-to-r from-red-400 to-red-600': guarantorInfo.status === 'rejected'
                        }"
                        :style="{ width: `${guarantorProgress}%` }"
                      >
                        <div class="absolute inset-0 bg-white/20 animate-pulse"></div>
                      </div>
                    </div>
                    <p class="text-sm text-emerald-700 mt-2 font-medium">
                      {{ hasGuarantor ? 
                          (guarantorInfo.status === 'verified' ? '🎉 Avalista completamente configurado y firmado' :
                           guarantorInfo.status === 'rejected' ? '❌ Avalista rechazó la invitación' :
                           '⚡ Avalista añadido, pendiente de firma') :
                          '📝 Sin avalista configurado' }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <!-- FIN SECCIÓN: AVALISTA -->

          </div>
        </div>
      </div>

      <!-- Modal para mostrar archivos subidos -->
      <div v-if="showFilesModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-lg font-bold text-gray-900">{{ selectedDocumentType?.name }}</h3>
            <button @click="closeFilesModal" class="text-gray-400 hover:text-gray-600">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div v-if="selectedFiles.length > 0" class="space-y-3">
            <div 
              v-for="file in selectedFiles" 
              :key="file.id" 
              class="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
            >
              <div class="flex items-center space-x-3">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span class="text-sm font-medium text-gray-700 truncate">{{ file.name }}</span>
              </div>
              <div class="flex items-center space-x-2">
                <a v-if="file.url" :href="file.url" target="_blank" class="text-indigo-600 hover:text-indigo-800 transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
                <button @click="deleteEssentialFile(selectedDocumentType.key, file.id)" class="text-gray-400 hover:text-red-500 transition-colors duration-200">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          
          <div v-else class="text-center py-4 text-gray-500">
            <p class="text-sm">No hay archivos subidos</p>
          </div>
          
          <div class="mt-6 flex justify-end">
            <button 
              @click="closeFilesModal" 
              class="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>

      <!-- Modal del Avalista -->
      <GuarantorSignatureModal
        v-if="showGuarantorModal"
        :existing-guarantor="guarantorInfo"
        @close="closeGuarantorModal"
        @guarantor-saved="onGuarantorSaved"
      />

      <!-- Modal de mensaje de limpieza -->
      <div v-if="showCleanupMessage" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
          <div class="text-center">
            <div class="text-lg font-semibold text-gray-900 mb-2">{{ cleanupMessage }}</div>
            <button 
              @click="showCleanupMessage = false" 
              class="mt-4 px-4 py-2 bg-white text-indigo-600 border border-indigo-200 rounded-full hover:bg-indigo-50 hover:shadow-md shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Entendido
            </button>
          </div>
        </div>
      </div>
    </div>
  </ErrorBoundary>
</template>

<script setup>
import { ref, computed, onMounted, watch, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { getAuth, onAuthStateChanged, updateProfile as updateAuthProfile, signOut } from 'firebase/auth'
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL, deleteObject, listAll } from 'firebase/storage'
import { doc, getDoc, updateDoc, onSnapshot } from 'firebase/firestore'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { db } from '../firebase'
import ErrorBoundary from './ErrorBoundary.vue'
import GuarantorSignatureModal from './GuarantorSignatureModal.vue'
import UserPayments from './UserPayments.vue'
import { validateDNIorNIE } from '../utils/dniValidator'
import { 
  DOCUMENT_TYPES,
  DOCUMENT_CONFIG,
  COUNTRY_DOCUMENTS,
  COUNTRIES,
  getAvailableDocuments,
  validateDocument,
  validateSpanishDocument,
  getCountriesByRegion
} from '../utils/documentValidator'

const router = useRouter()
const route = router.currentRoute
const { t } = useI18n()
const auth = getAuth()
const storage = getStorage()
const functions = getFunctions()

// Estado reactivo
const isLoading = ref(false)
const activeFilter = ref('all')
const currentUser = ref(null)
const isUpdatingProfile = ref(false)
const isInitialDataLoaded = ref(false) // Para evitar mostrar banner antes de cargar datos

// Sidebar y navegación
const showSidebar = ref(true)
const validSections = ['profile', 'documents', 'guarantor', 'payments', 'preferences']
const activeSection = ref('profile') // 'profile' | 'documents' | 'guarantor' | 'payments' | 'preferences'

// Leer sección del query param (?section=documents, etc.)
function applySectionFromRoute() {
  const section = route.value?.query?.section
  if (section && validSections.includes(section)) {
    activeSection.value = section
  }
}

// Watcher para cambios de ruta (ej: navegación desde el asistente)
watch(activeSection, (section) => {
  if (section === 'preferences' && currentUser.value) {
    loadDeleteAccountEligibility()
  }
})

watch(() => route.value?.query?.section, () => {
  applySectionFromRoute()
})

// Variable para el listener de Firestore
let userDocListener = null

// ========================================
// SECCIÓN: PAGOS
// ========================================
const userApplications = ref([])
const isLoadingPayments = ref(false)

// ========================================
// SECCIÓN 1: DATOS PERSONALES BÁSICOS
// ========================================
const fullName = ref('')
const phone = ref('')
const email = ref('')
const authProvider = ref('') // 'google' o 'email' o ''

// ========================================
// SECCIÓN 2: IDENTIFICACIÓN (DNI)
// ========================================
// Documento de identidad (sistema antiguo - mantener para compatibilidad)
const dni = ref('')
const dniError = ref('')
const dniValidationMessage = ref('')
const dniFrontPictureUrl = ref('')
const dniFrontPicture = ref(null)
const isUploadingDni = ref(false)

// Nuevo sistema de documentos internacionales
const documentCountry = ref('ES')  // País por defecto: España
const documentType = ref(DOCUMENT_TYPES.DNI_ES)  // Tipo por defecto: DNI español
const documentNumber = ref('')
const documentError = ref('')
const documentValidationMessage = ref('')
const documentFrontUrl = ref('')
const documentBackUrl = ref('')
const documentFrontFile = ref(null)
const documentBackFile = ref(null)

// Estados de verificación OCR
const ocrVerificationStatus = ref({
  front: { status: 'pending', message: '', confidence: 0 },
  back: { status: 'pending', message: '', confidence: 0 }
})
const isVerifyingOcr = ref(false)

// ========================================
// SECCIÓN 3: DIRECCIÓN Y ACOMPAÑANTE
// ========================================
const address = ref('')
const withOtherPerson = ref(false)
const otherPersonName = ref('')
const otherPersonDni = ref('')

// ========================================
// SECCIÓN 4: PERFIL DE CANDIDATO
// ========================================
const applicantDescription = ref('')
const monthlyIncome = ref(null)

// Preferencias de búsqueda de coliving
const preferredCity = ref('')
const isStudent = ref(false)

// Eliminación de cuenta (solo applicants)
const deleteAccountEligibility = ref(null)
const isLoadingDeleteEligibility = ref(false)
const isDeletingAccount = ref(false)
const deleteAccountConfirmed = ref(false)

// ========================================
// SECCIONES EXPANDIBLES
// ========================================
const expandedSections = ref({
  personalData: true,
  identification: false,
  searchPreferences: true,
  candidateProfile: true,
  companion: false,
  guarantor: true,
  documents: true
})

// Ciudades disponibles para la búsqueda (sincronizado con ColivingAdvancedSearch.vue)
const availableCities = [
  'Madrid',
  'Barcelona',
  'Valencia',
  'Sevilla',
  'Málaga',
  'Salamanca',
  'Bilbao',
  'Alicante',
  'Granada',
  'Pamplona',
  'Getafe',
  'Alcobendas'
]

// Estado para documentos esenciales
const essentialDocuments = ref([
  {
    key: 'payslips',
    name: 'Últimas 3 nóminas',
    description: 'Documentos que acrediten tus ingresos de los últimos 3 meses'
  },
  {
    key: 'work_life_report',
    name: 'Informe de vida laboral',
    description: 'Documento oficial de la Seguridad Social'
  },
  {
    key: 'tax_declaration',
    name: 'Declaración de la renta',
    description: 'Última declaración de la renta presentada'
  },
  {
    key: 'employment_contract',
    name: 'Contrato de trabajo',
    description: 'Contrato laboral actual vigente'
  },
  {
    key: 'additional_docs',
    name: 'Otros documentos de interés para el propietario',
    description: 'Documentos adicionales que puedan ser relevantes'
  }
])

const essentialDocumentsUploaded = ref({})
const essentialDocumentsUploading = ref({})

// Estado para modal de archivos
const showFilesModal = ref(false)
const selectedDocumentType = ref(null)
const selectedFiles = ref([])

// Variables para el avalista
const hasGuarantor = ref(false)
const guarantorInfo = ref({})
const showGuarantorModal = ref(false)

// Filtros disponibles para documentos esenciales
const documentFilters = computed(() => [
  { 
    key: 'all', 
    label: 'Todos', 
    count: essentialDocuments.value.length 
  },
  { 
    key: 'pending', 
    label: 'Pendientes', 
    count: essentialDocuments.value.filter(doc => getDocumentStatus(doc.key) === 'pending').length 
  },
  { 
    key: 'completed', 
    label: 'Completados', 
    count: essentialDocuments.value.filter(doc => getDocumentStatus(doc.key) === 'uploaded').length 
  }
])

// Documentos esenciales filtrados
const filteredEssentialDocuments = computed(() => {
  switch (activeFilter.value) {
    case 'pending':
      return essentialDocuments.value.filter(doc => getDocumentStatus(doc.key) === 'pending')
    case 'completed':
      return essentialDocuments.value.filter(doc => getDocumentStatus(doc.key) === 'uploaded')
    default:
      return essentialDocuments.value
  }
})

// Progreso de documentos esenciales
const essentialDocumentsProgress = computed(() => {
  const totalEssential = essentialDocuments.value.length
  const completedEssential = getCompletedEssentialDocuments()
  return totalEssential > 0 ? (completedEssential / totalEssential) * 100 : 0
})

// Progreso de datos personales básicos
const personalDataProgress = computed(() => {
  let completed = 0
  const total = 4  // Nombre + Teléfono + Fecha nacimiento + Email
  
  if (fullName.value?.trim().length > 0) completed++
  if (phone.value?.replace(/\s/g, '').length >= 9) completed++
  if (email.value) completed++
  
  return total > 0 ? (completed / total) * 100 : 0
})

// Progreso de identificación
// Computed: Países agrupados por región
const countriesByRegion = computed(() => getCountriesByRegion())

// Computed: Tipos de documento disponibles según el país seleccionado
const availableDocumentTypes = computed(() => {
  return getAvailableDocuments(documentCountry.value)
})

// Computed: Configuración del documento actual
const currentDocumentConfig = computed(() => {
  return DOCUMENT_CONFIG[documentType.value] || DOCUMENT_CONFIG[DOCUMENT_TYPES.DNI_ES]
})

// Computed: ¿Es un documento español que requiere OCR?
const requiresOcrVerification = computed(() => {
  return documentCountry.value === 'ES' && (
    documentType.value === DOCUMENT_TYPES.DNI_ES || 
    documentType.value === DOCUMENT_TYPES.NIE_ES
  )
})

const identificationProgress = computed(() => {
  let completed = 0
  let total = 1  // Solo número de documento
  
  // Verificar número de documento
  if (documentNumber.value && validateDocument(documentType.value, documentNumber.value, documentCountry.value)) completed++
  
  // Solo si es español (DNI/NIE), contar las fotos
  if (requiresOcrVerification.value) {
    total = 2  // Número + Foto frontal
    if (documentFrontUrl.value) completed++
  }
  
  return total > 0 ? (completed / total) * 100 : 0
})

// Progreso de datos del perfil (combina datos personales e identificación)
const profileDataProgress = computed(() => {
  const personalWeight = 0.5  // 50% datos personales
  const identificationWeight = 0.5  // 50% identificación
  
  return (personalDataProgress.value * personalWeight) + 
         (identificationProgress.value * identificationWeight)
})

// Progreso de preferencias (campos de preferencias y búsqueda)
const preferencesProgress = computed(() => {
  let completed = 0
  const total = 2  // Descripción + Estudiante (Ingresos y Ciudad son opcionales)
  
  if (applicantDescription.value && applicantDescription.value.trim().length > 0) completed++
  // isStudent siempre cuenta como completado (es un booleano, ya tiene valor)
  completed++
  
  return total > 0 ? (completed / total) * 100 : 0
})

// Progreso general combinado
const overallProfileProgress = computed(() => {
  const identificationWeight = 0.25  // 25% identificación (DNI + foto + dirección)
  const documentsWeight = 0.35       // 35% documentos esenciales
  const profileWeight = 0.20         // 20% perfil básico
  const preferencesWeight = 0.20     // 20% preferencias
  
  return (identificationProgress.value * identificationWeight) + 
         (essentialDocumentsProgress.value * documentsWeight) + 
         (profileDataProgress.value * profileWeight) +
         (preferencesProgress.value * preferencesWeight)
})

// Progreso del avalista
const guarantorProgress = computed(() => {
  if (!hasGuarantor.value) return 0
  if (!guarantorInfo.value || !guarantorInfo.value.fullName) return 25
  if (guarantorInfo.value.status === 'rejected') return 50 // Estado rechazado
  if (guarantorInfo.value.status === 'verified') return 100 // Completamente verificado
  return 75 // Pendiente de firma
})

// Métricas generales
const getCompletedItems = () => {
  return getCompletedEssentialDocuments() + getCompletedProfileFields() + getCompletedPreferencesFields()
}

const getTotalItems = () => {
  // 3 campos personales + campos de identificación + 2 campos de preferencias
  let identificationFields = 1  // Solo número de documento
  
  // Si es español (DNI/NIE), añadir foto del frente
  if (requiresOcrVerification.value) {
    identificationFields = 2  // número + frente
  }
  
  return essentialDocuments.value.length + 3 + identificationFields + 2
}

// Banner de perfil incompleto
const isProfileIncomplete = computed(() => {
  return overallProfileProgress.value < 100
})

/**
 * Puntuación estimada que ven los agentes cuando reciben la solicitud.
 * Refleja exactamente la misma lógica que el backend (applicantScoring.ts).
 * Máximo teórico: 250 pts (identity 50 + docs 50 + guarantor 50 + bonus 20 + duration 50 + seniority 20 + fifo 10)
 */
const agentViewScore = computed(() => {
  const MAX_SCORE = 250
  let score = 0
  const breakdown = {}

  // 1. Identidad — usa identificationProgress (ya maneja español/extranjero correctamente)
  const hasIdentity = identificationProgress.value >= 100
  breakdown.identity = hasIdentity ? 50 : 0
  score += breakdown.identity

  // 2. Documentos esenciales
  const hasDocuments = Object.values(essentialDocumentsUploaded.value || {}).some(
    (files) => Array.isArray(files) && files.length > 0
  )
  breakdown.documents = hasDocuments ? 50 : 0
  score += breakdown.documents

  // 3. Avalista
  breakdown.guarantor = hasGuarantor.value ? 50 : 0
  score += breakdown.guarantor

  // 4. Bonus perfil completo
  breakdown.completionBonus = hasIdentity && hasDocuments ? 20 : 0
  score += breakdown.completionBonus

  // 5. Duración (estancia media de 3 meses como referencia para mostrar al usuario)
  breakdown.duration = Math.round((3 / 12) * 50)
  score += breakdown.duration

  // 6. Antigüedad de cuenta
  breakdown.seniority = 0
  if (currentUser.value?.metadata?.creationTime) {
    const days = Math.floor(
      (Date.now() - new Date(currentUser.value.metadata.creationTime).getTime()) /
        (1000 * 60 * 60 * 24)
    )
    breakdown.seniority = Math.min(Math.floor(days / 3), 20)
    score += breakdown.seniority
  }

  const percentage = Math.round((score / MAX_SCORE) * 100)
  let level
  if (percentage >= 80) level = 'excellent'
  else if (percentage >= 55) level = 'high'
  else if (percentage >= 30) level = 'medium'
  else level = 'low'

  return { score, percentage, breakdown, level, MAX_SCORE }
})

const missingFields = computed(() => {
  const missing = []
  
  // Verificar campos OBLIGATORIOS (solo los esenciales para aplicar a colivings)
  if (!fullName.value?.trim()) missing.push(t('userPanel.banner.fields.fullName'))
  if (!phone.value || phone.value.replace(/\s/g, '').length < 9) missing.push(t('userPanel.banner.fields.phone'))
  if (!documentNumber.value || !validateDocument(documentType.value, documentNumber.value, documentCountry.value)) {
    missing.push(t('userPanel.banner.fields.dni'))
  }
  
  // Solo requerir fotos si es español (DNI/NIE)
  if (requiresOcrVerification.value) {
    if (!documentFrontUrl.value) missing.push(t('userPanel.banner.fields.dniPhoto'))
  }
  
  // Campos opcionales eliminados: address, preferredCity, monthlyIncome
  
  return missing
})

const getCompletedProfileFields = () => {
  let completed = 0
  // Datos personales
  if (fullName.value?.trim().length > 0) completed++
  if (phone.value?.replace(/\s/g, '').length >= 9) completed++
  if (email.value) completed++
  
  // Identificación - Nuevo sistema
  if (documentNumber.value && validateDocument(documentType.value, documentNumber.value, documentCountry.value)) completed++
  
  // Solo contar fotos si es español (DNI/NIE)
  if (requiresOcrVerification.value) {
    if (documentFrontUrl.value) completed++
  }
  
  return completed
}

const getCompletedPreferencesFields = () => {
  let completed = 0
  if (applicantDescription.value && applicantDescription.value.trim().length > 0) completed++
  // isStudent siempre cuenta como completado
  completed++
  // monthlyIncome y preferredCity son opcionales, no se cuentan
  return completed
}

// Métodos para documentos esenciales
const getDocumentStatus = (docKey) => {
  if (essentialDocumentsUploading.value[docKey]) return 'uploading'
  if (essentialDocumentsUploaded.value[docKey] && essentialDocumentsUploaded.value[docKey].length > 0) return 'uploaded'
  return 'pending'
}

const getUploadedFilesCount = (docKey) => {
  return essentialDocumentsUploaded.value[docKey]?.length || 0
}

const isUploadingDocument = (docKey) => {
  return essentialDocumentsUploading.value[docKey] || false
}

const getCompletedEssentialDocuments = () => {
  return essentialDocuments.value.filter(doc => 
    getDocumentStatus(doc.key) === 'uploaded'
  ).length
}

// Métodos de gestión de archivos
const triggerEssentialFileInput = (docKey) => {
  const fileInput = document.getElementById(`essential-file-${docKey}`)
  if (fileInput) {
    fileInput.click()
  }
}

const handleEssentialDocumentUpload = async (docKey, event) => {
  const files = event.target.files
  if (!files || !files.length) return
  
  if (!currentUser.value) {
    console.error('Usuario no autenticado')
    alert('Debes estar autenticado para subir documentos')
    return
  }
  
  // Activar estado de carga
  essentialDocumentsUploading.value[docKey] = true
  
  try {
    const uploadedFiles = []
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      
      // Validar tipo y tamaño de archivo
      const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']
      if (!validTypes.includes(file.type)) {
        alert(`Tipo de archivo no permitido: ${file.type}`)
        continue
      }
      
      if (file.size > 10 * 1024 * 1024) { // 10MB
        alert(`El archivo ${file.name} es demasiado grande (máximo 10MB)`)
        continue
      }
      
      // Subir archivo a Firebase Storage
      const fileName = `${Date.now()}_${file.name}`
      const fileRef = storageRef(storage, `essential_documents/${currentUser.value.uid}/${docKey}/${fileName}`)
      
      await uploadBytes(fileRef, file)
      const downloadURL = await getDownloadURL(fileRef)
      
      uploadedFiles.push({
        id: fileName,
        name: file.name,
        url: downloadURL,
        uploadedAt: new Date(),
        docType: docKey
      })
    }
    
    // Actualizar estado con archivos subidos
    if (!essentialDocumentsUploaded.value[docKey]) {
      essentialDocumentsUploaded.value[docKey] = []
    }
    essentialDocumentsUploaded.value[docKey].push(...uploadedFiles)
    
    // Actualizar localStorage para persistencia
    localStorage.setItem('essentialDocuments', JSON.stringify(essentialDocumentsUploaded.value))

    // Escribir flag documentsStatus en Firestore para que los agentes no
    // tengan que hacer listAll() en Storage por cada candidato.
    if (uploadedFiles.length > 0 && currentUser.value) {
      try {
        const userRef = doc(db, 'users', currentUser.value.uid)
        await updateDoc(userRef, {
          [`documentsStatus.${docKey}`]: true,
          documentsStatusUpdatedAt: new Date()
        })
      } catch (fsError) {
        console.warn('[UserPanel] No se pudo actualizar documentsStatus en Firestore:', fsError)
      }
    }
    
  } catch (error) {
    console.error('Error subiendo documentos esenciales:', error)
    alert('Error subiendo documentos. Por favor, inténtalo de nuevo.')
  } finally {
    // Desactivar estado de carga
    essentialDocumentsUploading.value[docKey] = false
    // Limpiar el input
    event.target.value = ''
  }
}

const showUploadedFiles = (docKey) => {
  const files = essentialDocumentsUploaded.value[docKey] || []
  if (files.length === 0) return
  
  selectedDocumentType.value = essentialDocuments.value.find(d => d.key === docKey)
  selectedFiles.value = files
  showFilesModal.value = true
}

const closeFilesModal = () => {
  showFilesModal.value = false
  selectedDocumentType.value = null
  selectedFiles.value = []
}

const deleteEssentialFile = async (docKey, fileId) => {
  try {
    if (!currentUser.value) return
    
    // Eliminar de Firebase Storage
    const fileRef = storageRef(storage, `essential_documents/${currentUser.value.uid}/${docKey}/${fileId}`)
    
    try {
      await deleteObject(fileRef)
    } catch (storageError) {
      // Manejar específicamente el caso de archivo no encontrado
      if (storageError.code === 'storage/object-not-found') {
        console.warn(`⚠️ Archivo no encontrado en Storage (probablemente eliminado externamente): ${fileId}`)
        // Continuar con la limpieza del localStorage aunque el archivo no exista en Storage
      } else {
        // Para otros errores de Storage, relanzar el error
        throw storageError
      }
    }
    
    // Actualizar estado local (esto se ejecuta siempre, incluso si el archivo no existía en Storage)
    if (essentialDocumentsUploaded.value[docKey]) {
      const initialCount = essentialDocumentsUploaded.value[docKey].length
      essentialDocumentsUploaded.value[docKey] = 
        essentialDocumentsUploaded.value[docKey].filter(file => file.id !== fileId)
      
      const finalCount = essentialDocumentsUploaded.value[docKey].length
    }
    
    // Actualizar localStorage
    localStorage.setItem('essentialDocuments', JSON.stringify(essentialDocumentsUploaded.value))
    
    // Actualizar lista de archivos en el modal
    selectedFiles.value = selectedFiles.value.filter(file => file.id !== fileId)
    
  } catch (error) {
    console.error('❌ Error eliminando archivo:', error)
    
    // Mensaje de error más específico
    if (error.code === 'storage/object-not-found') {
      alert('⚠️ El archivo ya no existe en el servidor, pero se ha limpiado de tu lista local.')
    } else {
      alert('❌ Error eliminando archivo. Por favor, inténtalo de nuevo.')
    }
  }
}

// Métodos para gestión del perfil
const updateProfile = async () => {
  if (!currentUser.value) {
    alert('Debes estar autenticado para actualizar tu perfil')
    return
  }
  
  // Validaciones - usar el nuevo sistema de documentos
  if (documentNumber.value && !validateDocumentInput(documentNumber.value)) {
    alert(documentError.value || 'El documento introducido no es válido')
    return
  }
  
  isUpdatingProfile.value = true
  
  try {
    const userRef = doc(db, 'users', currentUser.value.uid)
    
    // Subir imágenes del documento SOLO si es español (DNI/NIE)
    let frontUrl = documentFrontUrl.value
    let backUrl = documentBackUrl.value
    
    if (requiresOcrVerification.value) {
      // Solo usuarios españoles suben fotos
      if (documentFrontFile.value && documentFrontFile.value instanceof File) {
        const fileName = `${Date.now()}_document_front.jpg`
        const frontRef = storageRef(storage, `dni/${currentUser.value.uid}/front/${fileName}`)
        await uploadBytes(frontRef, documentFrontFile.value)
        frontUrl = await getDownloadURL(frontRef)
      }

      if (documentBackFile.value && documentBackFile.value instanceof File) {
        const fileName = `${Date.now()}_document_back.jpg`
        const backRef = storageRef(storage, `dni/${currentUser.value.uid}/back/${fileName}`)
        await uploadBytes(backRef, documentBackFile.value)
        backUrl = await getDownloadURL(backRef)
      }
    } else {
      // Usuarios no españoles: no tienen fotos
      frontUrl = ''
      backUrl = ''
    }
    
    const updateData = {
      // Datos personales
      fullName: fullName.value || '',
      phone: phone.value?.replace(/\s/g, '') || '',
      
      // Identificación - Sistema internacional
      documentType: documentType.value,
      documentNumber: documentNumber.value || '',
      documentCountry: documentCountry.value,
      documentFrontUrl: frontUrl || '',
      documentBackUrl: backUrl || '',
      
      // Estado de verificación
      documentOcrVerified: requiresOcrVerification.value ? (ocrVerificationStatus.value.front.status === 'verified') : false,
      documentVerificationStatus: requiresOcrVerification.value 
        ? (ocrVerificationStatus.value.front.status === 'verified' ? 'verified' : 'pending')
        : 'verified',  // No españoles se marcan como verificados automáticamente
      lastDocumentVerification: new Date(),
      
      // Campos antiguos (mantener para compatibilidad con código existente)
      dni: documentNumber.value || '',
      dniFrontPictureUrl: frontUrl || '',
      dniOcrVerified: requiresOcrVerification.value ? (ocrVerificationStatus.value.front.status === 'verified') : false,
      
      // Dirección y acompañante
      address: address.value || '',
      withOtherPerson: withOtherPerson.value || false,
      otherPersonName: withOtherPerson.value ? (otherPersonName.value || '') : '',
      otherPersonDni: withOtherPerson.value ? (otherPersonDni.value || '') : '',
      
      // Perfil de candidato
      applicantDescription: applicantDescription.value || '',
      monthlyIncome: monthlyIncome.value || null,
      
      // Preferencias de búsqueda
      preferredCity: preferredCity.value || '',
      isStudent: isStudent.value || false,
      
      // Metadata
      lastProfileUpdate: new Date()
    }
    
    // Determinar la fase del perfil y si está completo
    const hasPhase2 = fullName.value && phone.value
    
    // Para españoles: requiere número + foto + verificación OCR
    // Para no españoles: solo requiere número válido (sin fotos)
    let hasPhase3 = false
    let isDocumentVerified = false
    
    if (documentCountry.value === 'ES') {
      // Usuario ESPAÑOL: requiere foto + OCR
      // Auto-detectar tipo (DNI o NIE) antes de guardar
      if (documentNumber.value && documentNumber.value.length === 9) {
        const firstChar = documentNumber.value.charAt(0)
        if (/^[XYZ]/.test(firstChar)) {
          updateData.documentType = DOCUMENT_TYPES.NIE_ES
        } else if (/^[0-9]/.test(firstChar)) {
          updateData.documentType = DOCUMENT_TYPES.DNI_ES
        }
      }
      
      const hasDocumentComplete = documentNumber.value && frontUrl
      hasPhase3 = hasDocumentComplete
      isDocumentVerified = ocrVerificationStatus.value.front.status === 'verified'
    } else {
      // Usuario NO ESPAÑOL: solo requiere número válido
      hasPhase3 = documentNumber.value && validateDocument(documentType.value, documentNumber.value, documentCountry.value)
      isDocumentVerified = hasPhase3  // Auto-verificado si tiene número válido
      
      // Marcar como verificado automáticamente para no españoles
      if (isDocumentVerified) {
        updateData.documentVerificationStatus = 'verified'
        updateData.documentOcrVerified = false  // No se usó OCR
        updateData.documentVerifiedAt = new Date()
      }
    }
    
    if (hasPhase3) {
      updateData.profilePhase = 3
      updateData.hasCompletedProfile = isDocumentVerified
    } else if (hasPhase2) {
      updateData.profilePhase = 2
    }
    
    // También guardar en localStorage para acceso rápido desde ColivingAdvancedSearch
    try {
      localStorage.setItem('userSearchPreferences', JSON.stringify({
        preferences: {
          preferredCity: preferredCity.value || '',
          isStudent: isStudent.value || false,
          monthlyIncome: monthlyIncome.value || null
        },
        timestamp: Date.now()
      }))
    } catch (e) {
      console.warn('No se pudo guardar en localStorage:', e)
    }
    
    await updateDoc(userRef, updateData)
    
    // Actualizar displayName en Firebase Auth
    if (fullName.value && currentUser.value) {
      try {
        await updateAuthProfile(currentUser.value, { displayName: fullName.value })
      } catch (authError) {
        console.warn('Error actualizando displayName:', authError)
      }
    }
    
    // Mostrar mensaje de éxito
    alert('✅ Perfil actualizado correctamente')
    
  } catch (error) {
    console.error('Error actualizando perfil:', error)
    alert('❌ Error al actualizar el perfil. Inténtalo de nuevo.')
  } finally {
    isUpdatingProfile.value = false
  }
}

// Métodos para gestión del avalista
const openGuarantorModal = () => {
  showGuarantorModal.value = true
}

const closeGuarantorModal = () => {
  showGuarantorModal.value = false
}

const editGuarantor = () => {
  showGuarantorModal.value = true
}

const onGuarantorSaved = (guarantorData) => {
  guarantorInfo.value = guarantorData
  hasGuarantor.value = true
  showGuarantorModal.value = false
}

const removeGuarantor = async () => {
  if (!confirm('¿Estás seguro de que quieres eliminar el avalista? Esta acción no se puede deshacer.')) {
    return
  }

  try {
    const userRef = doc(db, 'users', currentUser.value.uid)
    await updateDoc(userRef, {
      hasGuarantor: false,
      guarantorInfo: {},
      guarantorRemovedAt: new Date()
    })

    hasGuarantor.value = false
    guarantorInfo.value = {}
  } catch (error) {
    console.error('Error eliminando avalista:', error)
    alert('❌ Error al eliminar el avalista. Inténtalo de nuevo.')
  }
}

const getRelationshipLabel = (type) => {
  const labels = {
    parent: 'Padre/Madre',
    sibling: 'Hermano/a',
    spouse: 'Cónyuge',
    friend: 'Amigo/a',
    other: 'Otro'
  }
  return labels[type] || type
}

const formatDate = (date) => {
  if (!date) return ''
  const d = date.toDate ? date.toDate() : new Date(date)
  return d.toLocaleDateString('es-ES')
}

// ========================================
// FUNCIONES PARA FECHA DE NACIMIENTO
// ========================================
// FUNCIONES PARA DNI Y VERIFICACIÓN OCR
// ========================================
// Función separada para validar DNI (similar a Profile.vue)
const validateDniInput = (value) => {
  if (!value) {
    dniError.value = t('userPanel.validation.dniRequired')
    return false
  }

  // Convertir a mayúsculas
  value = value.toUpperCase().trim()

  // Verificar formato básico: 8 números + letra O X/Y/Z + 7 números + letra
  if (!/^[0-9]{8}[A-Z]$/.test(value) && !/^[XYZ][0-9]{7}[A-Z]$/.test(value)) {
    dniError.value = t('userPanel.validation.dniInvalidFormat')
    return false
  }

  // Validar DNI/NIE con el algoritmo de letra de control
  if (!validateDNIorNIE(value)) {
    dniError.value = t('userPanel.validation.dniInvalid')
    return false
  }

  // DNI válido
  dniError.value = ''
  dniValidationMessage.value = t('userPanel.validation.dniValid')
  return true
}

const handleDniInput = (event) => {
  let value = event.target.value.toUpperCase().trim()
  
  // Eliminar espacios y caracteres no válidos
  value = value.replace(/[^0-9A-Z]/g, '')
  
  // Actualizar el valor
  dni.value = value
  
  // Limpiar mensajes previos mientras se escribe
  dniError.value = ''
  dniValidationMessage.value = ''
  
  // Solo validar cuando el DNI/NIE esté completo (9 caracteres con la letra)
  // Esto evita mostrar errores mientras el usuario está escribiendo
  if (value.length === 9) {
    validateDniInput(value)
  }
}

const triggerDniFrontInput = () => {
  document.getElementById('dni-front-input')?.click()
}

const handleDniFrontUpload = async (event) => {
  const file = event.target.files[0]
  if (!file) return
  
  if (!file.type.match('image.*')) {
    alert('El archivo debe ser una imagen')
    return
  }
  
  if (file.size > 5 * 1024 * 1024) {
    alert('La imagen es demasiado grande (máximo 5MB)')
    return
  }
  
  dniFrontPicture.value = file
  
  // Vista previa
  const reader = new FileReader()
  reader.onload = (e) => {
    dniFrontPictureUrl.value = e.target.result
  }
  reader.readAsDataURL(file)
  
  // Verificar con OCR si el documento está validado (sistema antiguo - deprecado)
  // Esta función ya no se usa con el nuevo sistema de documentos internacionales
}

// Función deprecada - mantener por compatibilidad con código antiguo
const verifyDniWithOcr = async (imageFile, documentType) => {
  // Esta función ya no se usa con el nuevo sistema internacional
  // Se mantiene para evitar errores en código legacy
  console.warn('verifyDniWithOcr está deprecada, usar verifyDocumentWithOcr en su lugar')
  return false
}

const retryOcrVerification = async (side = 'front') => {
  if (side === 'front' && documentFrontFile.value) {
    await verifyDocumentWithOcr(documentFrontFile.value, 'front')
  } else if (side === 'back' && documentBackFile.value) {
    await verifyDocumentWithOcr(documentBackFile.value, 'back')
  }
}

// ========================================
// FUNCIONES DEL NUEVO SISTEMA DE DOCUMENTOS INTERNACIONALES
// ========================================

const handleCountryChange = () => {
  // Limpiar mensajes de validación
  documentError.value = ''
  documentValidationMessage.value = ''
  documentNumber.value = ''
  
  if (documentCountry.value === 'ES') {
    // España: no hay selector, por defecto DNI (se auto-detecta al escribir)
    documentType.value = DOCUMENT_TYPES.DNI_ES
  } else {
    // Otros países: seleccionar el primer tipo disponible
    const availableTypes = getAvailableDocuments(documentCountry.value)
    if (availableTypes.length > 0) {
      documentType.value = availableTypes[0].value
    }
    
    // Limpiar fotos y estado OCR (no necesarias para extranjeros)
    documentFrontUrl.value = ''
    documentBackUrl.value = ''
    documentFrontFile.value = null
    documentBackFile.value = null
    ocrVerificationStatus.value.front.status = 'pending'
    ocrVerificationStatus.value.front.message = ''
  }
}

const handleDocumentTypeChange = () => {
  // Limpiar mensajes de validación
  documentError.value = ''
  documentValidationMessage.value = ''
  documentNumber.value = ''
  
  // Limpiar imagen del reverso si el nuevo tipo no lo requiere
  if (!currentDocumentConfig.value.requiresBack) {
    documentBackUrl.value = ''
    documentBackFile.value = null
  }
}

const handleDocumentNumberInput = (event) => {
  let value = event.target?.value || event
  if (typeof value === 'string') {
    value = value.toUpperCase().trim()
    
    // Eliminar espacios y caracteres especiales
    value = value.replace(/[^0-9A-Z]/g, '')
    
    documentNumber.value = value
    documentError.value = ''
    documentValidationMessage.value = ''
    
    // Si es España, auto-detectar si es DNI o NIE
    if (documentCountry.value === 'ES' && value.length === 9) {
      const firstChar = value.charAt(0)
      if (/^[XYZ]/.test(firstChar)) {
        documentType.value = DOCUMENT_TYPES.NIE_ES
      } else if (/^[0-9]/.test(firstChar)) {
        documentType.value = DOCUMENT_TYPES.DNI_ES
      }
      validateDocumentInput(value)
    } else if (value.length >= 6) {
      // Para otros países, validar cuando tenga longitud mínima
      validateDocumentInput(value)
    }
  }
}

const validateDocumentInput = (value) => {
  if (!value) {
    documentError.value = 'El número de documento es obligatorio'
    return false
  }
  
  let isValid = false
  
  // Para España, validar DNI o NIE automáticamente
  if (documentCountry.value === 'ES') {
    isValid = validateSpanishDocument(value)
    if (!isValid) {
      documentError.value = 'El DNI/NIE introducido no es válido'
      return false
    }
    // Auto-detectar el tipo para guardarlo en Firestore
    const firstChar = value.charAt(0)
    if (/^[XYZ]/.test(firstChar)) {
      documentType.value = DOCUMENT_TYPES.NIE_ES
      documentValidationMessage.value = 'NIE válido'
    } else {
      documentType.value = DOCUMENT_TYPES.DNI_ES
      documentValidationMessage.value = 'DNI válido'
    }
  } else {
    // Para otros países, usar el validador específico
    isValid = validateDocument(documentType.value, value, documentCountry.value)
    if (!isValid) {
      documentError.value = `El ${currentDocumentConfig.value.label} introducido no es válido`
      return false
    }
    documentValidationMessage.value = `${currentDocumentConfig.value.label} válido`
  }
  
  documentError.value = ''
  return true
}

const triggerDocumentFrontInput = () => {
  document.getElementById('document-front-input')?.click()
}

const triggerDocumentBackInput = () => {
  document.getElementById('document-back-input')?.click()
}

const handleDocumentFrontUpload = async (event) => {
  const file = event.target.files[0]
  if (!file) return
  
  if (!file.type.match('image.*')) {
    alert('El archivo debe ser una imagen')
    return
  }
  
  if (file.size > 5 * 1024 * 1024) {
    alert('La imagen es demasiado grande (máximo 5MB)')
    return
  }
  
  documentFrontFile.value = file
  
  // Vista previa
  const reader = new FileReader()
  reader.onload = (e) => {
    documentFrontUrl.value = e.target.result
  }
  reader.readAsDataURL(file)
  
  // Verificar con OCR SOLO si es español (DNI/NIE)
  if (requiresOcrVerification.value && documentNumber.value && validateDocumentInput(documentNumber.value)) {
    await verifyDocumentWithOcr(file, 'front')
  }
}

const handleDocumentBackUpload = async (event) => {
  const file = event.target.files[0]
  if (!file) return
  
  if (!file.type.match('image.*')) {
    alert('El archivo debe ser una imagen')
    return
  }
  
  if (file.size > 5 * 1024 * 1024) {
    alert('La imagen es demasiado grande (máximo 5MB)')
    return
  }
  
  documentBackFile.value = file
  
  // Vista previa
  const reader = new FileReader()
  reader.onload = (e) => {
    documentBackUrl.value = e.target.result
  }
  reader.readAsDataURL(file)
  
  // El reverso NO se verifica con OCR en ningún caso
}

const verifyDocumentWithOcr = async (imageFile, documentSide) => {
  // Esta función SOLO se llama para documentos españoles (DNI/NIE)
  if (!documentNumber.value || !validateDocumentInput(documentNumber.value)) {
    alert('Por favor, introduce un número de documento válido antes de subir la foto')
    return false
  }
  
  if (!currentUser.value) {
    alert(t('userPanel.toasts.authError'))
    return false
  }
  
  try {
    isVerifyingOcr.value = true
    ocrVerificationStatus.value[documentSide].status = 'verifying'
    ocrVerificationStatus.value[documentSide].message = 'Verificando documento...'
    
    const base64Image = await new Promise((resolve) => {
      const reader = new FileReader()
      reader.onload = (e) => resolve(e.target.result)
      reader.readAsDataURL(imageFile)
    })
    
    // Usar la Cloud Function existente para DNI/NIE español
    const verifyDniDocument = httpsCallable(functions, 'verifyDniDocument')
    const result = await verifyDniDocument({
      imageBase64: base64Image,
      manualDni: documentNumber.value,
      documentType: documentSide,
      userId: currentUser.value.uid
    })
    
    const { success, confidence, matches, message } = result.data
    
    if (success && matches) {
      ocrVerificationStatus.value[documentSide].status = 'verified'
      ocrVerificationStatus.value[documentSide].message = 'DNI/NIE verificado correctamente'
      ocrVerificationStatus.value[documentSide].confidence = confidence
      return true
    } else {
      ocrVerificationStatus.value[documentSide].status = 'failed'
      ocrVerificationStatus.value[documentSide].message = message || 'Error en la verificación'
      ocrVerificationStatus.value[documentSide].confidence = confidence || 0
      return false
    }
  } catch (error) {
    console.error('Error en verificación OCR:', error)
    ocrVerificationStatus.value[documentSide].status = 'failed'
    ocrVerificationStatus.value[documentSide].message = error.message || 'Error interno de verificación'
    return false
  } finally {
    isVerifyingOcr.value = false
  }
}

// Función para formatear teléfono
const formatPhone = (event) => {
  let value = event.target.value.replace(/\D/g, '')
  if (value.length > 9) value = value.substring(0, 9)
  
  if (value.length >= 6) {
    phone.value = `${value.substring(0, 3)} ${value.substring(3, 6)} ${value.substring(6)}`
  } else if (value.length >= 3) {
    phone.value = `${value.substring(0, 3)} ${value.substring(3)}`
  } else {
    phone.value = value
  }
}

// Toggle para secciones expandibles
const toggleSection = (section) => {
  expandedSections.value[section] = !expandedSections.value[section]
}

// Funciones para la navegación del sidebar
const getSectionTitle = () => {
  const titles = {
    profile: t('userPanel.sections.profile.title'),
    preferences: t('userPanel.sections.preferences.title'),
    documents: t('userPanel.sections.documents.title'),
    guarantor: t('userPanel.sections.guarantor.title'),
    payments: t('userPanel.sections.payments.title')
  }
  return titles[activeSection.value] || 'Mi Panel'
}

const getSectionDescription = () => {
  const descriptions = {
    profile: t('userPanel.sections.profile.description'),
    preferences: t('userPanel.sections.preferences.description'),
    documents: t('userPanel.sections.documents.description'),
    guarantor: t('userPanel.sections.guarantor.description'),
    payments: t('userPanel.sections.payments.description')
  }
  return descriptions[activeSection.value] || ''
}

const getSectionProgressLabel = () => {
  const labels = {
    profile: t('userPanel.sections.profile.progressLabel'),
    preferences: t('userPanel.sections.preferences.progressLabel'),
    documents: t('userPanel.sections.documents.progressLabel'),
    guarantor: t('userPanel.sections.guarantor.progressLabel'),
    payments: t('userPanel.sections.payments.progressLabel')
  }
  return labels[activeSection.value] || ''
}

const getSectionProgress = () => {
  switch (activeSection.value) {
    case 'profile':
      return profileDataProgress.value
    case 'preferences':
      return preferencesProgress.value
    case 'payments':
      return totalPaymentsMade.value > 0 ? 100 : 0
    case 'documents':
      return essentialDocumentsProgress.value
    case 'guarantor':
      return guarantorProgress.value
    default:
      return 0
  }
}

const loadUserProfile = async () => {
  if (!currentUser.value) return
  
  try {
    const userRef = doc(db, 'users', currentUser.value.uid)
    const userDoc = await getDoc(userRef)
    
    if (userDoc.exists()) {
      const userData = userDoc.data()
      
      // Datos personales básicos
      fullName.value = userData.fullName || currentUser.value.displayName || ''
      phone.value = userData.phone || ''
      email.value = userData.email || currentUser.value.email || ''
      authProvider.value = userData.authProvider || '' // 'google', 'email', etc.
      
      // Identificación - Nuevo sistema (con fallback al antiguo)
      if (userData.documentCountry && userData.documentType) {
        // Usuario con el nuevo sistema de documentos internacionales
        documentCountry.value = userData.documentCountry
        documentType.value = userData.documentType
        documentNumber.value = userData.documentNumber || ''
        documentFrontUrl.value = userData.documentFrontUrl || ''
        documentBackUrl.value = userData.documentBackUrl || ''
        
        if (userData.documentVerificationStatus === 'verified') {
          ocrVerificationStatus.value.front.status = 'verified'
          ocrVerificationStatus.value.front.message = 'Documento verificado'
        } else if (userData.documentVerificationStatus === 'manual_review') {
          ocrVerificationStatus.value.front.status = 'manual_review'
          ocrVerificationStatus.value.front.message = 'Pendiente de revisión'
        }
      } else if (userData.dni) {
        // Usuario con sistema antiguo - migrar a nuevo sistema
        documentCountry.value = 'ES'
        documentType.value = userData.dni.match(/^[XYZ]/) ? DOCUMENT_TYPES.NIE_ES : DOCUMENT_TYPES.DNI_ES
        documentNumber.value = userData.dni
        documentFrontUrl.value = userData.dniFrontPictureUrl || ''
        
        if (userData.dniOcrVerified) {
          ocrVerificationStatus.value.front.status = 'verified'
          ocrVerificationStatus.value.front.message = 'DNI verificado'
        }
        
        // Mantener compatibilidad con sistema antiguo
        dni.value = userData.dni || ''
        dniFrontPictureUrl.value = userData.dniFrontPictureUrl || ''
      }
      
      // Dirección y acompañante
      address.value = userData.address || ''
      withOtherPerson.value = userData.withOtherPerson || false
      otherPersonName.value = userData.otherPersonName || ''
      otherPersonDni.value = userData.otherPersonDni || ''
      
      // Perfil de candidato
      applicantDescription.value = userData.applicantDescription || ''
      monthlyIncome.value = userData.monthlyIncome || null
      
      // Cargar preferencias de búsqueda
      preferredCity.value = userData.preferredCity || ''
      isStudent.value = userData.isStudent || false
      
      // Cargar información del avalista
      hasGuarantor.value = userData.hasGuarantor || false
      guarantorInfo.value = userData.guarantorInfo || {}
      
      // Sincronizar con localStorage para acceso rápido
      try {
        localStorage.setItem('userSearchPreferences', JSON.stringify({
          preferences: {
            preferredCity: userData.preferredCity || '',
            isStudent: userData.isStudent || false,
            monthlyIncome: userData.monthlyIncome || null
          },
          timestamp: Date.now()
        }))
      } catch (e) {
        console.warn('No se pudo sincronizar con localStorage:', e)
      }
    }
  } catch (error) {
    console.error('Error cargando perfil de usuario:', error)
  }
}

const setupUserListener = () => {
  if (!currentUser.value) return
  
  // Limpiar listener anterior si existe
  if (userDocListener) {
    userDocListener()
    userDocListener = null
  }
  
  try {
    const userRef = doc(db, 'users', currentUser.value.uid)
    
    // Configurar listener en tiempo real
    userDocListener = onSnapshot(userRef, (userDoc) => {
      if (userDoc.exists()) {
        const userData = userDoc.data()
        
        // Datos personales básicos
        fullName.value = userData.fullName || currentUser.value?.displayName || ''
        phone.value = userData.phone || ''
        email.value = userData.email || currentUser.value?.email || ''
        authProvider.value = userData.authProvider || ''
        
        // Identificación - Nuevo sistema (con fallback al antiguo)
        if (userData.documentCountry && userData.documentType) {
          // Usuario con el nuevo sistema de documentos internacionales
          if (!documentCountry.value) documentCountry.value = userData.documentCountry
          if (!documentType.value) documentType.value = userData.documentType
          if (!documentNumber.value) documentNumber.value = userData.documentNumber || ''
          if (!documentFrontUrl.value) documentFrontUrl.value = userData.documentFrontUrl || ''
          if (!documentBackUrl.value) documentBackUrl.value = userData.documentBackUrl || ''
          
          if (userData.documentVerificationStatus === 'verified') {
            ocrVerificationStatus.value.front.status = 'verified'
          } else if (userData.documentVerificationStatus === 'manual_review') {
            ocrVerificationStatus.value.front.status = 'manual_review'
          }
        } else if (userData.dni && !documentNumber.value) {
          // Usuario con sistema antiguo - migrar a nuevo sistema
          documentCountry.value = 'ES'
          documentType.value = userData.dni.match(/^[XYZ]/) ? DOCUMENT_TYPES.NIE_ES : DOCUMENT_TYPES.DNI_ES
          documentNumber.value = userData.dni
          documentFrontUrl.value = userData.dniFrontPictureUrl || ''
          
          if (userData.dniOcrVerified) {
            ocrVerificationStatus.value.front.status = 'verified'
          }
        }
        
        // Dirección y acompañante
        address.value = userData.address || ''
        withOtherPerson.value = userData.withOtherPerson || false
        otherPersonName.value = userData.otherPersonName || ''
        otherPersonDni.value = userData.otherPersonDni || ''
        
        // Perfil de candidato
        applicantDescription.value = userData.applicantDescription || ''
        monthlyIncome.value = userData.monthlyIncome || null
        
        // Cargar preferencias de búsqueda
        preferredCity.value = userData.preferredCity || ''
        isStudent.value = userData.isStudent || false
        
        // Cargar información del avalista (se actualiza automáticamente cuando el avalista firma)
        hasGuarantor.value = userData.hasGuarantor || false
        guarantorInfo.value = userData.guarantorInfo || {}
      } else {
        // Usuario nuevo sin documento en Firestore - inicializar con valores por defecto
        fullName.value = currentUser.value?.displayName || ''
        email.value = currentUser.value?.email || ''
      }
      
      // Marcar que los datos iniciales se han cargado (después de la primera ejecución)
      if (!isInitialDataLoaded.value) {
        isInitialDataLoaded.value = true
      }
    }, (error) => {
      console.error('Error en listener de usuario:', error)
      // Marcar como cargado incluso si hay error para no bloquear la UI
      if (!isInitialDataLoaded.value) {
        isInitialDataLoaded.value = true
      }
    })
    
  } catch (error) {
    console.error('Error configurando listener de usuario:', error)
  }
}

const cleanupListener = () => {
  if (userDocListener) {
    userDocListener()
    userDocListener = null
  }
  // Resetear el estado de carga inicial cuando se limpia el listener
  isInitialDataLoaded.value = false
}

// CORREGIDO: Cargar documentos desde Firebase Storage (fuente real) en lugar de solo localStorage
const loadEssentialDocuments = async () => {
  if (!currentUser.value) {
    essentialDocumentsUploaded.value = {}
    return
  }
  
  try {
    // Limpiar estado previo
    essentialDocumentsUploaded.value = {}
    
    // Cargar documentos de CADA tipo desde Firebase Storage
    for (const docType of essentialDocuments.value) {
      try {
        const docTypeRef = storageRef(storage, `essential_documents/${currentUser.value.uid}/${docType.key}`)
        const listResult = await listAll(docTypeRef)
        
        // Si hay archivos en este tipo de documento
        if (listResult.items.length > 0) {
          const filesForType = []
          
          for (const item of listResult.items) {
            try {
              const downloadURL = await getDownloadURL(item)
              filesForType.push({
                id: item.name,
                name: item.name,
                url: downloadURL,
                uploadedAt: null, // No tenemos fecha exacta desde Storage
                docType: docType.key
              })
            } catch (urlError) {
              console.warn(`No se pudo obtener URL para ${item.name}:`, urlError)
            }
          }
          
          if (filesForType.length > 0) {
            essentialDocumentsUploaded.value[docType.key] = filesForType
          }
        }
      } catch (docTypeError) {
        // Este tipo de documento no tiene archivos o hay un error - continuar con el siguiente
      }
    }
    
    // Actualizar localStorage para sincronización
    localStorage.setItem('essentialDocuments', JSON.stringify(essentialDocumentsUploaded.value))
    
  } catch (error) {
    console.error('Error cargando documentos esenciales desde Firebase Storage:', error)
    // Intentar cargar desde localStorage como fallback
    try {
      const saved = localStorage.getItem('essentialDocuments')
      if (saved) {
        essentialDocumentsUploaded.value = JSON.parse(saved)
      }
    } catch (localError) {
      console.error('Error cargando desde localStorage:', localError)
    }
  }
}

// Estado para operaciones de limpieza
const isCleaningPhantomFiles = ref(false)
const cleanupMessage = ref('')
const showCleanupMessage = ref(false)

// NUEVA: Función para mostrar mensajes de limpieza
const showCleanupFeedback = (message, type = 'info') => {
  cleanupMessage.value = message
  showCleanupMessage.value = true
  
  // Auto-ocultar después de 4 segundos
  setTimeout(() => {
    showCleanupMessage.value = false
  }, 4000)
}

// NUEVA: Función para verificar y limpiar archivos fantasma
const verifyAndCleanPhantomFiles = async () => {
  if (!currentUser.value) return
  
  isCleaningPhantomFiles.value = true
  
  try {
    let hasChanges = false
    let phantomCount = 0
    const { getDownloadURL } = await import('firebase/storage')
    
    for (const [docKey, files] of Object.entries(essentialDocumentsUploaded.value)) {
      if (!Array.isArray(files)) continue
      
      const verifiedFiles = []
      
      for (const file of files) {
        try {
          // Intentar obtener la URL del archivo para verificar que existe
          const fileRef = storageRef(storage, `essential_documents/${currentUser.value.uid}/${docKey}/${file.id}`)
          await getDownloadURL(fileRef)
          
          // Si llegamos aquí, el archivo existe
          verifiedFiles.push(file)
          
        } catch (error) {
          if (error.code === 'storage/object-not-found') {
            console.warn(`🧹 Archivo fantasma detectado y eliminado: ${file.name} (${file.id})`)
            hasChanges = true
            phantomCount++
          } else {
            // Para otros errores, mantener el archivo (puede ser un problema temporal)
            console.warn(`⚠️ Error verificando archivo ${file.name}:`, error.code)
            verifiedFiles.push(file)
          }
        }
      }
      
      // Actualizar solo si hubo cambios
      if (verifiedFiles.length !== files.length) {
        essentialDocumentsUploaded.value[docKey] = verifiedFiles
      }
    }
    
    // Guardar cambios en localStorage si hubo limpieza
    if (hasChanges) {
      localStorage.setItem('essentialDocuments', JSON.stringify(essentialDocumentsUploaded.value))
    }
  } catch (error) {
    console.error('❌ Error durante la verificación:', error)
  } finally {
    isCleaningPhantomFiles.value = false
  }
}

const refreshDocuments = async () => {
  await loadEssentialDocuments()
  
  // Verificar y limpiar archivos fantasma
  if (currentUser.value) {
    await verifyAndCleanPhantomFiles()
  }
  
  // El listener ya maneja la actualización automática del perfil
  // pero podemos forzar una recarga si es necesario
  if (currentUser.value) {
    setupUserListener()
  }
}

// Lifecycle
// ========================================
// FUNCIONES DE PAGOS
// ========================================
// NOTA: La lógica de UI de pagos está en UserPayments.vue
// UserPanel mantiene la carga de datos para:
// 1. Mostrar el contador de pagos en el sidebar
// 2. Calcular el progreso de la sección de pagos
// 3. Pasar los datos al componente UserPayments

/**
 * Cargar todas las aplicaciones del usuario con sus pagos
 * Lee de Firestore: colivingApplications WHERE applicantId == uid
 * Incluye paymentConfirmations[] con pagos de Stripe y manuales
 */
async function loadUserApplications() {
  if (!currentUser.value) return
  
  isLoadingPayments.value = true
  try {
    const { collection, query, where, getDocs, orderBy } = await import('firebase/firestore')
    
    // Obtener todas las aplicaciones del usuario
    const applicationsQuery = query(
      collection(db, 'colivingApplications'),
      where('applicantId', '==', currentUser.value.uid),
      orderBy('createdAt', 'desc')
    )
    
    const applicationsSnapshot = await getDocs(applicationsQuery)
    const applications = []
    
    for (const docSnapshot of applicationsSnapshot.docs) {
      const appData = docSnapshot.data()
      
      // Obtener título del coliving
      let colivingTitle = 'Coliving'
      if (appData.colivingId) {
        try {
          const colivingDoc = await getDoc(doc(db, 'colivings', appData.colivingId))
          if (colivingDoc.exists()) {
            colivingTitle = colivingDoc.data().title || 'Coliving'
          }
        } catch (error) {
          console.error('Error cargando título del coliving:', error)
        }
      }
      
      applications.push({
        id: docSnapshot.id,
        colivingTitle,
        ...appData
      })
    }
    
    userApplications.value = applications
  } catch (error) {
    console.error('Error cargando aplicaciones del usuario:', error)
  } finally {
    isLoadingPayments.value = false
  }
}

/**
 * Computed: Total de pagos realizados
 */
const totalPaymentsMade = computed(() => {
  return userApplications.value.reduce((total, app) => {
    return total + (app.paymentConfirmations?.length || 0)
  }, 0)
})

/**
 * Computed: Monto total pagado
 */
const totalAmountPaid = computed(() => {
  return userApplications.value.reduce((total, app) => {
    const appTotal = (app.paymentConfirmations || []).reduce((sum, payment) => {
      return sum + (payment.amount || 0)
    }, 0)
    return total + appTotal
  }, 0)
})

/**
 * Cargar si el applicant puede eliminar su cuenta
 */
async function loadDeleteAccountEligibility() {
  if (!currentUser.value) return

  isLoadingDeleteEligibility.value = true
  try {
    const token = await currentUser.value.getIdToken()
    const response = await fetch('/api/users/me/delete-account/eligibility', {
      headers: { Authorization: `Bearer ${token}` },
    })

    if (response.ok) {
      deleteAccountEligibility.value = await response.json()
    } else {
      deleteAccountEligibility.value = {
        canDelete: false,
        reason: t('userPanel.accountDeletion.checkError'),
      }
    }
  } catch (error) {
    console.error('[UserPanel] Error comprobando elegibilidad de borrado:', error)
    deleteAccountEligibility.value = {
      canDelete: false,
      reason: t('userPanel.accountDeletion.checkError'),
    }
  } finally {
    isLoadingDeleteEligibility.value = false
  }
}

async function handleDeleteAccount() {
  if (!currentUser.value || !deleteAccountConfirmed.value || isDeletingAccount.value) return

  const confirmed = window.confirm(t('userPanel.accountDeletion.finalConfirm'))
  if (!confirmed) return

  isDeletingAccount.value = true
  try {
    const token = await currentUser.value.getIdToken()
    const response = await fetch('/api/users/me', {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ confirm: true }),
    })

    const data = await response.json().catch(() => ({}))

    if (!response.ok) {
      alert(data.error || t('userPanel.accountDeletion.error'))
      if (response.status === 409) {
        await loadDeleteAccountEligibility()
      }
      return
    }

    cleanupListener()
    await signOut(auth)
    localStorage.removeItem('userSearchPreferences')
    localStorage.removeItem('authUser')
    router.push('/')
  } catch (error) {
    console.error('[UserPanel] Error eliminando cuenta:', error)
    alert(t('userPanel.accountDeletion.error'))
  } finally {
    isDeletingAccount.value = false
  }
}

onMounted(() => {
  // Leer sección del query param al montar (deep-link desde asistente)
  applySectionFromRoute()

  if (activeSection.value === 'preferences' && currentUser.value) {
    loadDeleteAccountEligibility()
  }

  // Escuchar cambios en el estado de autenticación
  onAuthStateChanged(auth, async (user) => {
    currentUser.value = user
    if (user) {
      await loadEssentialDocuments()
      await loadUserApplications()
      
      // Verificar y limpiar archivos fantasma automáticamente al iniciar sesión
      setTimeout(async () => {
        await verifyAndCleanPhantomFiles()
      }, 1000) // Esperar 1 segundo para que cargue todo
      
      setupUserListener()

      if (activeSection.value === 'preferences') {
        loadDeleteAccountEligibility()
      }
    } else {
      cleanupListener()
      essentialDocumentsUploaded.value = {}
      userApplications.value = []
      applicantDescription.value = ''
      monthlyIncome.value = null
      hasGuarantor.value = false
      guarantorInfo.value = {}
    }
  })
})

// Cleanup cuando el componente se desmonta
onUnmounted(() => {
  cleanupListener()
})

// Watchers
watch(() => currentUser.value, async (newUser) => {
  if (newUser) {
    await loadEssentialDocuments()
    
    // Verificar archivos fantasma cuando cambia el usuario
    setTimeout(async () => {
      await verifyAndCleanPhantomFiles()
    }, 500)
    
    setupUserListener()
  } else {
    cleanupListener()
  }
}, { immediate: false })
</script>

<style scoped>
/* Animaciones personalizadas */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Mejoras visuales */
.document-card {
  transition: all 0.3s ease-in-out;
}

.document-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

/* Botones con efectos hover mejorados */
button:hover {
  transform: translateY(-1px);
}

button:active {
  transform: translateY(0);
}

/* Mejoras de accesibilidad */
button:focus {
  outline: none;
}

/* Animación suave para el progreso */
.bg-gray-900 {
  transition: width 0.5s ease-out;
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .document-filters {
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .filter-button {
    justify-content: center;
  }
  
  .px-4.py-2 {
    padding: 0.5rem 1rem;
  }
  
  .rounded-xl {
    border-radius: 0.75rem;
  }
}

/* Mejoras para tarjetas */
.bg-white.rounded-xl {
  transition: all 0.2s ease-in-out;
}

.bg-white.rounded-xl:hover {
  border-color: #9ca3af;
}

/* Estados de loading mejorados */
.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: .5;
  }
}

/* Ocultar scrollbar pero mantener funcionalidad de scroll */
.no-scrollbar::-webkit-scrollbar {
  display: none;
}

.no-scrollbar {
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
}
</style> 